import React, { useEffect, useState, useRef, useCallback } from "react";
import { Badge } from "../components/ui/badge";
import { Link, useNavigate } from "react-router-dom";

import {
  Users,
  Calendar,
  Clock,
  CheckSquare,
  TrendingUp,
  FileText,
  Bell,
  UserPlus,
  LogIn,
  LogOut,
  IndianRupee,
  ChevronRight,
  ChevronLeft,
  ArrowRight,
  Download,
  ClipboardList,
  Activity,
  Wallet,
  FolderOpen,
  PlusCircle,
} from "lucide-react";

import { getDashboardData, getRecentActivities } from "../api/dashboard";
import { tasksApi } from "../api/tasks";
import { getLeaves } from "../api/leaves";
import { getAttendanceByDate, getAttendanceDayWise, getDailyPresence } from "../api/attendance";
import { countPresentForDay } from "../utils/attendanceTrend";
import { projectsApi } from "../api/projects";
import { salaryApi } from "../api/salary";
import { useAuth } from "../contexts/AuthContext";
import { useAccess } from "../contexts/AccessContext";
import { useToast } from "../hooks/useToast";

// Inline count-up component
const Counter: React.FC<{ value: number; suffix?: string; prefix?: string; duration?: number }> = ({ value, suffix = "", prefix = "", duration = 1400 }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) { setDisplay(value); return; }
    const start = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(value * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <span ref={ref}>{prefix}{Math.round(display).toLocaleString("en-IN")}{suffix}</span>;
};

/* ──────────────── Inline SVG charts ──────────────── */
type Segment = { label: string; value: number; color: string };

// Animated donut/pie with segments, labels, and a center stat
const DonutChart: React.FC<{ segments: Segment[]; size?: number; thickness?: number; centerLabel?: string; centerValue?: React.ReactNode }> = ({
  segments,
  size = 160,
  thickness = 18,
  centerLabel,
  centerValue,
}) => {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let acc = 0;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--gt-surface-2)" strokeWidth={thickness} />
        {segments.map((seg, i) => {
          const len = (seg.value / total) * c;
          const offset = c - acc;
          acc += len;
          return (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth={thickness}
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={offset}
              strokeLinecap="butt"
              style={{ transition: "stroke-dasharray 1100ms cubic-bezier(0.22,1,0.36,1), stroke-dashoffset 1100ms cubic-bezier(0.22,1,0.36,1)" }}
            />
          );
        })}
      </svg>
      {(centerValue || centerLabel) && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {centerValue && <div className="dash-serif" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 32, fontWeight: 500, letterSpacing: "-0.025em", lineHeight: 1, color: "var(--gt-text-primary)" }}>{centerValue}</div>}
          {centerLabel && <div className="dash-eyebrow" style={{ marginTop: 6 }}>{centerLabel}</div>}
        </div>
      )}
    </div>
  );
};

// Vertical bars trend chart — gradient fills, optional per-bar "today" highlight
const BarChart: React.FC<{ data: { label: string; value: number; sub?: string; isToday?: boolean; dateNum?: number; status?: "present" | "absent" | "leave" | "none"; isActive?: boolean }[]; max?: number; height?: number; valueFormat?: (v: number) => string }> = ({
  data,
  max,
  height = 140,
  valueFormat,
}) => {
  const m = max ?? Math.max(1, ...data.map((d) => d.value));
  const FILL = "linear-gradient(180deg, var(--gt-primary-soft), var(--gt-primary))";
  const FILL_TODAY = "linear-gradient(180deg, var(--gt-online), #1f6f4a)";
  // Absent → red, On leave → yellow. These days carry zero hours, so we flag
  // the whole bar at full height in the status colour instead of leaving it
  // empty; present days keep the hours-proportional green bar.
  const FILL_ABSENT = "linear-gradient(180deg, #f87171, #dc2626)";
  const FILL_LEAVE = "linear-gradient(180deg, #fbbf24, #f59e0b)";
  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex items-end gap-2.5 w-full flex-1" style={{ minHeight: height }}>
        {data.map((d, i) => {
          const flagged = d.status === "absent" || d.status === "leave";
          // A "present" day with no logged hours yet (clocked in, not clocked
          // out) still gets a small visible bar so attendance shows immediately
          // after clock-in instead of waiting for clock-out to total the hours.
          const presentZero = d.status === "present" && d.value <= 0;
          const h = flagged ? 100 : presentZero ? 8 : Math.max((d.value / m) * 100, d.value > 0 ? 5 : 0);
          const fill =
            d.status === "absent" ? FILL_ABSENT
              : d.status === "leave" ? FILL_LEAVE
                : d.isToday ? FILL_TODAY : FILL;
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1.5 group" style={{ height: "100%" }}>
              <div className="dash-mono flex items-center justify-center gap-1" style={{ fontSize: 10.5, color: d.isActive ? "var(--gt-online)" : d.isToday ? "var(--gt-primary)" : "var(--gt-text-secondary)", fontWeight: 700, opacity: d.value > 0 || flagged || presentZero || d.isActive ? 1 : 0.4 }}>
                {/* Live "clocked in right now" pulse — only on the active day. */}
                {d.isActive && (
                  <span className="relative flex h-1.5 w-1.5" title="Active now">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: "var(--gt-online)" }} />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: "var(--gt-online)" }} />
                  </span>
                )}
                {d.status === "absent" ? "Absent" : d.status === "leave" ? "Leave" : valueFormat ? valueFormat(d.value) : d.value}
              </div>
              <div className="w-full relative" style={{ flex: 1, background: "var(--gt-surface-2)", borderRadius: "10px 10px 6px 6px", display: "flex", alignItems: "flex-end", overflow: "hidden" }}>
                <div
                  className="w-full group-hover:brightness-110"
                  style={{
                    height: `${h}%`,
                    background: fill,
                    borderRadius: "10px 10px 0 0",
                    transition: "height 1100ms cubic-bezier(0.22,1,0.36,1)",
                    boxShadow: "inset 0 1.5px 0 rgba(255,255,255,0.22)",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-2.5 w-full mt-2.5">
        {data.map((d, i) => (
          <div key={i} className="flex-1 text-center dash-mono" style={{ fontSize: 9.5, color: d.isToday ? "var(--gt-primary)" : "var(--gt-text-tertiary)", fontWeight: d.isToday ? 600 : 400, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            <div>{d.label}</div>
            {d.dateNum != null && (
              <div style={{ fontSize: 11, marginTop: 3, fontWeight: d.isToday ? 700 : 500, opacity: d.isToday ? 1 : 0.75, letterSpacing: 0 }}>{d.dateNum}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Horizontal stacked bar — sums to 100%
const StackedBar: React.FC<{ segments: Segment[]; height?: number }> = ({ segments, height = 12 }) => {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  return (
    <div className="w-full">
      <div className="flex w-full overflow-hidden rounded-full" style={{ height, background: "var(--gt-surface-2)" }}>
        {segments.map((seg, i) => (
          <div
            key={i}
            title={`${seg.label} · ${seg.value}`}
            style={{
              width: `${(seg.value / total) * 100}%`,
              background: seg.color,
              transition: "width 1100ms cubic-bezier(0.22,1,0.36,1)",
            }}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3">
        {segments.map((seg, i) => {
          const pct = Math.round((seg.value / total) * 100);
          return (
            <div key={i} className="flex items-center gap-1.5 dash-mono" style={{ fontSize: 11, color: "var(--gt-text-secondary)" }}>
              <span className="w-2 h-2 rounded-full" style={{ background: seg.color }} />
              <span style={{ fontWeight: 600 }}>{seg.label}</span>
              <span style={{ color: "var(--gt-text-tertiary)" }}>· {seg.value} ({pct}%)</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Tiny inline sparkline for module tiles
const Sparkline: React.FC<{ data: number[]; width?: number; height?: number; color?: string }> = ({ data, width = 80, height = 24, color = "var(--gt-primary)" }) => {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1 || 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x},${y}`;
  }).join(" ");
  const areaPoints = `0,${height} ${points} ${width},${height}`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      <polygon points={areaPoints} fill={color} opacity={0.12} />
      <polyline points={points} fill="none" stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={(data.length - 1) / (data.length - 1 || 1) * width} cy={height - ((data[data.length - 1] - min) / range) * height} r={2.5} fill={color} />
    </svg>
  );
};
// Month grid of colored day cells — green = present, red = absent, yellow =
// leave, neutral = weekend / holiday / future / no data. Weeks start on Monday
// to match the weekly productivity chart beside it.
const PRESENCE_COLORS: Record<"present" | "absent" | "leave" | "none", { bg: string; fg: string }> = {
  present: { bg: "var(--gt-online)", fg: "var(--gt-white)" },
  absent: { bg: "var(--gt-red-600)", fg: "var(--gt-white)" },
  leave: { bg: "var(--gt-amber-500)", fg: "var(--gt-white)" },
  none: { bg: "var(--gt-surface-2)", fg: "var(--gt-text-tertiary)" },
};
const MonthlyPresenceCalendar: React.FC<{
  leadBlanks: number;
  days: { n: number; status: "present" | "absent" | "leave" | "none"; isToday: boolean }[];
}> = ({ leadBlanks, days }) => {
  const weekdayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <div>
      <div className="grid grid-cols-7 gap-1 mb-1.5">
        {weekdayLabels.map((w, i) => (
          <div key={i} className="text-center dash-mono" style={{ fontSize: 9, color: "var(--gt-text-tertiary)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
            {w[0]}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: leadBlanks }).map((_, i) => (
          <div key={`blank-${i}`} />
        ))}
        {days.map((d) => {
          const c = PRESENCE_COLORS[d.status];
          return (
            <div
              key={d.n}
              title={`${d.n}: ${d.status === "none" ? "—" : d.status}`}
              className="flex items-center justify-center"
              style={{
                height: 30,
                borderRadius: 6,
                background: c.bg,
                color: c.fg,
                fontSize: 11,
                fontWeight: 600,
                boxShadow: d.isToday ? "0 0 0 2px var(--gt-text-primary)" : "none",
              }}
            >
              {d.n}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2.5" style={{ fontSize: 11, color: "var(--gt-text-secondary)" }}>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: PRESENCE_COLORS.present.bg }} /> Present</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: PRESENCE_COLORS.absent.bg }} /> Absent</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: PRESENCE_COLORS.leave.bg }} /> Leave</span>
      </div>
    </div>
  );
};

// Admin Monthly Presence Calendar — each day coloured by the % of employees
// present that day: green ≥90, yellow ≥75, orange ≥50, red <50. Neutral days
// (future / weekend / holiday) render on the surface tone. Weeks start Monday.
const ORG_PRESENCE_BANDS: { label: string; color: string }[] = [
  { label: "90–100%", color: "#16a34a" }, // green
  { label: "75–90%", color: "#eab308" },  // yellow
  { label: "50–75%", color: "#f97316" },  // orange
  { label: "< 50%", color: "#dc2626" },   // red
];
const orgPresenceColor = (pct: number): string =>
  pct >= 90 ? "#16a34a" : pct >= 75 ? "#eab308" : pct >= 50 ? "#f97316" : "#dc2626";

const OrgMonthlyPresenceCalendar: React.FC<{
  leadBlanks: number;
  days: { n: number; percent: number; colored: boolean; isToday: boolean }[];
}> = ({ leadBlanks, days }) => {
  const weekdayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <div>
      <div className="grid grid-cols-7 gap-1 mb-1.5">
        {weekdayLabels.map((w, i) => (
          <div key={i} className="text-center dash-mono" style={{ fontSize: 9, color: "var(--gt-text-tertiary)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
            {w[0]}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: leadBlanks }).map((_, i) => (
          <div key={`blank-${i}`} />
        ))}
        {days.map((d) => {
          const bg = d.colored ? orgPresenceColor(d.percent) : "var(--gt-surface-2)";
          const fg = d.colored ? "var(--gt-white)" : "var(--gt-text-tertiary)";
          return (
            <div
              key={d.n}
              title={d.colored ? `${d.n}: ${d.percent}% present` : `${d.n}: —`}
              className="flex items-center justify-center"
              style={{
                height: 30,
                borderRadius: 6,
                background: bg,
                color: fg,
                fontSize: 11,
                fontWeight: 600,
                boxShadow: d.isToday ? "0 0 0 2px var(--gt-text-primary)" : "none",
              }}
            >
              {d.n}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2.5" style={{ fontSize: 11, color: "var(--gt-text-secondary)" }}>
        {ORG_PRESENCE_BANDS.map((b, i) => (
          <span key={i} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: b.color }} /> {b.label}
          </span>
        ))}
      </div>
    </div>
  );
};

// Grouped column chart — two bars per day (e.g. tasks assigned vs completed).
// Fills its container height like BarChart so it sits flush in a tile.
const WTF_COLORS = {
  assigned: "linear-gradient(180deg, var(--gt-primary-soft), var(--gt-primary))",
  completed: "linear-gradient(180deg, var(--gt-online), #1f6f4a)",
};
const GroupedBarChart: React.FC<{
  data: { label: string; dateNum?: number; isToday?: boolean; assigned: number; completed: number }[];
  height?: number;
}> = ({ data, height = 150 }) => {
  const m = Math.max(1, ...data.flatMap((d) => [d.assigned, d.completed]));
  const bar = (val: number, fill: string) => {
    const h = Math.max((val / m) * 100, val > 0 ? 6 : 0);
    return (
      <div className="relative" style={{ width: "44%", height: "100%", background: "var(--gt-surface-2)", borderRadius: "8px 8px 4px 4px", display: "flex", alignItems: "flex-end", overflow: "hidden" }}>
        <div className="w-full" style={{ height: `${h}%`, background: fill, borderRadius: "8px 8px 0 0", transition: "height 1100ms cubic-bezier(0.22,1,0.36,1)", boxShadow: "inset 0 1.5px 0 rgba(255,255,255,0.22)" }} />
      </div>
    );
  };
  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex items-end gap-2 w-full flex-1" style={{ minHeight: height }}>
        {data.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1.5" style={{ height: "100%" }}>
            <div className="dash-mono" style={{ fontSize: 10, fontWeight: 700, color: d.isToday ? "var(--gt-primary)" : "var(--gt-text-secondary)", opacity: d.assigned + d.completed > 0 ? 1 : 0.35 }}>
              {d.assigned}/{d.completed}
            </div>
            <div className="flex items-end justify-center gap-1 w-full" style={{ flex: 1 }}>
              {bar(d.assigned, WTF_COLORS.assigned)}
              {bar(d.completed, WTF_COLORS.completed)}
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 w-full mt-2.5">
        {data.map((d, i) => (
          <div key={i} className="flex-1 text-center dash-mono" style={{ fontSize: 9.5, color: d.isToday ? "var(--gt-primary)" : "var(--gt-text-tertiary)", fontWeight: d.isToday ? 600 : 400, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            <div>{d.label}</div>
            {d.dateNum != null && (
              <div style={{ fontSize: 11, marginTop: 3, fontWeight: d.isToday ? 700 : 500, opacity: d.isToday ? 1 : 0.75, letterSpacing: 0 }}>{d.dateNum}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
/* ──────────────── /Inline SVG charts ──────────────── */

export function Dashboard() {
  const [stats, setStats] = useState<any>(null);
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [displayActivities, setDisplayActivities] = useState<any[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [taskStats, setTaskStats] = useState<any>(null);
  const [leavesData, setLeavesData] = useState<any[]>([]);
  const [attendanceTrend, setAttendanceTrend] = useState<{ label: string; value: number; isToday?: boolean; dateNum?: number; status?: "present" | "absent" | "leave" | "none"; isActive?: boolean }[]>([]);
  const [attendanceWeekLabel, setAttendanceWeekLabel] = useState<string>("");
  // Current-month presence calendar (employee side only): one entry per day with
  // a present/absent/leave/none status, plus the month label and presence %.
  const [presenceMonth, setPresenceMonth] = useState<{
    label: string;
    pct: number;
    leadBlanks: number;
    days: { n: number; status: "present" | "absent" | "leave" | "none"; isToday: boolean }[];
  } | null>(null);
  // Employee task charts (current user).
  const [weeklyFlow, setWeeklyFlow] = useState<{ label: string; dateNum: number; isToday: boolean; assigned: number; completed: number }[]>([]);
  const [weeklyFlowLabel, setWeeklyFlowLabel] = useState<string>("");
  const [monthlyProd, setMonthlyProd] = useState<{ total: number; todo: number; in_progress: number; completed: number; reviewed: number; productivity: number } | null>(null);

  // Org-level admin charts: Weekly Task Flow (created vs completed this week,
  // org-wide), Monthly Productivity (org this month), and the Monthly Presence
  // Calendar (% of employees present per day). Populated by the admin effect.
  // `assigned` on orgWeeklyFlow carries the CREATED count so it feeds the shared
  // GroupedBarChart; the card relabels its legend "Created" for admin.
  const [orgWeeklyFlow, setOrgWeeklyFlow] = useState<{ label: string; dateNum: number; isToday: boolean; assigned: number; completed: number }[]>([]);
  const [orgWeeklyFlowLabel, setOrgWeeklyFlowLabel] = useState<string>("");
  const [orgMonthlyProd, setOrgMonthlyProd] = useState<{ total: number; todo: number; in_progress: number; completed: number; reviewed: number; productivity: number } | null>(null);
  const [orgPresenceMonth, setOrgPresenceMonth] = useState<{
    label: string;
    avgPct: number;
    leadBlanks: number;
    days: { n: number; percent: number; colored: boolean; isToday: boolean }[];
  } | null>(null);
  const [projectsList, setProjectsList] = useState<any[]>([]);
  const [salaryStatus, setSalaryStatus] = useState<any>(null);
  const [nextHoliday, setNextHoliday] = useState<{ date: string; name: string } | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Everyone except admin sees their own weekly working hours ("Weekly Productivity
  // Hours"); admin keeps the org-wide presence headcount ("Daily presence").
  const isPersonalHoursView = (user?.role || "").toLowerCase() !== "admin";
  // The focused EMPLOYEE dashboard: Quick Actions on top, only the 4 task/attendance
  // charts, greeting + module grid kept, everything else removed. Future role/name
  // management can extend this per-chart (each chart tile carries a data-chart name).
  // Interns are the employee tier — they get the same focused employee
  // dashboard (own task/attendance charts), not the admin/org layout.
  const isEmployee = ["employee", "intern"].includes((user?.role || "").toLowerCase());

  // Roles whose hero ledger drops the org-wide "Total Employees" tile and show
  // their three personal metrics instead. The Upcoming Holiday is no longer a
  // ledger tile — it lives in the header calendar widget (visible to every role,
  // including employees, whose ledger is hidden entirely).
  const HOLIDAY_TILE_ROLES = new Set([
    "employee", "intern", "supervisor", "manager", "hr", "hr head", "hr admin", "hod", "team lead",
  ]);
  const dropsHeadcountTile = HOLIDAY_TILE_ROLES.has((user?.role || "").toLowerCase());
  const ledgerCols = dropsHeadcountTile ? 3 : 4;

  // ── Per-card access control ─────────────────────────────────────────
  // Each dashboard card is gated by an Access profile (panelCatalog `dash-*`
  // features). When a profile DOESN'T specify a card (or no profile is assigned)
  // the role default applies — so the layout above keeps working out of the box.
  // Visible = the role sees the card; Manage = its click-through / actions are on.
  const { hasAccess, permissions } = useAccess();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const CARD_ROLE_DEFAULT: Record<string, boolean> = {
    "dash-quick-actions": true,
    "dash-weekly-productivity": true,
    // Admin gets ORG-level variants of these three (calendar coloured by %
    // present, org task flow, org productivity); non-admins keep the personal
    // versions. An explicit Access profile still overrides the default.
    "dash-monthly-presence": isPersonalHoursView || isAdmin,
    "dash-weekly-task-flow": isEmployee || isAdmin,
    // Shown for admin too so the half-width chart tiles pair up evenly (6 cards,
    // no lone/gap tile). It's org-wide data (per-project completion %).
    "dash-project-execution": isEmployee || isAdmin,
    "dash-monthly-productivity": isEmployee || isAdmin,
    "dash-tasks-priority": isAdmin,
    "dash-tasks-status": !isEmployee,
    "dash-leaves-pipeline": !isEmployee,
    "dash-workforce": !isEmployee,
    "dash-projects": !isEmployee,
    "dash-attendance-health": !isEmployee,
    "dash-org-pulse": !isEmployee,
  };
  const cardVisible = (key: string): boolean => {
    const p = hasAccess ? (permissions as any)[key] : undefined;
    return p === undefined ? (CARD_ROLE_DEFAULT[key] ?? true) : p.visible === true;
  };
  const cardManage = (key: string): boolean => {
    const p = hasAccess ? (permissions as any)[key] : undefined;
    if (p === undefined) return CARD_ROLE_DEFAULT[key] ?? true; // actions on by default
    return p.visible === true && p.level === "manage";
  };
  // A card's click-through is enabled only at Manage level; at View level the
  // card still renders but isn't clickable.
  const cardNav = (key: string, path: string) =>
    cardManage(key) ? () => navigate(path) : undefined;

  const fetchData = async () => {
    setLoading(true);
    try {
      const todayDate = new Date().toISOString().split("T")[0];

      // Fetch dashboard stats from existing API
      const dashboardResponse = await getDashboardData(todayDate);

      // Fetch open tasks count from tasks API
      let openTasksCount = 0;
      try {
        const tasksResponse = await tasksApi.list({ page_size: 100 });
        const allTasks = tasksResponse.data.items || [];
        openTasksCount = allTasks.filter(t => !['DONE', 'CANCELLED'].includes(t.status)).length;
      } catch (taskError) {
        console.error("Error fetching tasks:", taskError);
      }

      // Extract stats from dashboard response
      if (dashboardResponse) {
        setStats({
          total_employees: dashboardResponse.total_employees,
          activeEmployees: dashboardResponse.activeEmployees,
          pending_leaves: dashboardResponse.pending_leaves,
          todays_present: dashboardResponse.todays_present,
          openTasks: openTasksCount,
          completedTasks: dashboardResponse.completedTasks,
        });
      }

      // Fetch recent activities from the API. No date filter — we want the most
      // recent activity across days (newest first), not just today's.
      const activitiesResponse = await getRecentActivities(undefined, 50);

      // Handle different response structures for activities
      let activitiesData = [];
      if (activitiesResponse && activitiesResponse.activities) {
        activitiesData = activitiesResponse.activities;
      } else if (Array.isArray(activitiesResponse)) {
        activitiesData = activitiesResponse;
      } else if (activitiesResponse && activitiesResponse.data) {
        activitiesData = activitiesResponse.data;
      } else {
        activitiesData = [];
      }

      // Process and shuffle activities to get a mixed display
      const processedActivities = processAndShuffleActivities(activitiesData);
      setActivities(processedActivities);

      // Set initial display activities (first page of 4)
      setDisplayActivities(processedActivities.slice(0, 4));
      setCurrentPageIndex(0);

      // Fetch additional analytics data in parallel — all best-effort. The weekly
      // attendance/productivity chart loads separately (role-dependent) in its own
      // effect below, since `user` (and thus role) isn't known on first render.
      const today = new Date();
      const yyyy = String(today.getFullYear());
      const mm = String(today.getMonth() + 1).padStart(2, "0");

      const [tStatsRes, leavesRes, projectsRes, salaryRes] = await Promise.allSettled([
        tasksApi.getDashboardStats(),
        getLeaves(),
        projectsApi.list({ page_size: 100 }),
        salaryApi.getSalaryStatus(yyyy, mm, []),
      ]);

      if (tStatsRes.status === "fulfilled") {
        const data = (tStatsRes.value as any)?.data || tStatsRes.value;
        setTaskStats(data || null);
      }
      if (leavesRes.status === "fulfilled") {
        setLeavesData(leavesRes.value?.leaves || []);
      }
      if (projectsRes.status === "fulfilled") {
        const raw = (projectsRes.value as any)?.data;
        const items: any[] = Array.isArray(raw) ? raw : raw?.items || raw?.projects || raw?.data || [];
        setProjectsList(items);
      }
      if (salaryRes.status === "fulfilled") {
        const raw = (salaryRes.value as any)?.data || salaryRes.value;
        setSalaryStatus(raw?.status || raw || null);
      }

    } catch (error: any) {
      console.error("Error fetching dashboard data:", error);
      toast({
        // title: "Error",
        description: error.message || "Failed to load dashboard data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Process and SHUFFLE all activities to get a true mix of different types
  const processAndShuffleActivities = (rawActivities: any[]) => {
    const processedActivities: any[] = [];

    for (const activity of rawActivities) {
      const type = activity.type?.toLowerCase();
      const title = (activity.title || "").toLowerCase();
      const description = (activity.description || "").toLowerCase();
      // const employee_name = activity.employee_name || "Employee";
      const employee_name =
  activity.employee_name && activity.employee_name !== "Unknown"
    ? activity.employee_name
    : "Admin";
      const timestamp = activity.timestamp;

      // Handle Attendance (Clock In/Out)
      if (type === "attendance") {
        const status = activity.meta?.status || "";
        if (status === "Clock Out" || title.includes("clocked out")) {
          processedActivities.push({
            ...activity,
            displayType: "clock_out",
            displayMessage: `${employee_name} clocked out`,
            icon: "clock_out",
            color: "red",
            time: timestamp,
            badge: "Checked Out",
            badgeColor: "red"
          });
        } else if (status === "Clock In" || title.includes("clocked in")) {
          processedActivities.push({
            ...activity,
            displayType: "clock_in",
            displayMessage: `${employee_name} clocked in`,
            icon: "clock_in",
            color: "green",
            time: timestamp,
            badge: "Checked In",
            badgeColor: "green"
          });
        } else if (status === "Additional In" || description.includes("additional in")) {
          processedActivities.push({
            ...activity,
            displayType: "clock_in",
            displayMessage: `${employee_name} clocked in (Additional)`,
            icon: "clock_in",
            color: "green",
            time: timestamp,
            badge: "Additional In",
            badgeColor: "green"
          });
        }
      }
      // Handle Leave Activities
      else if (type === "leave") {
        const leaveStatus = activity.meta?.status || "";
        const leaveType = activity.meta?.leave_type || "";
        const days = activity.meta?.total_days || "";

        if (leaveStatus === "APPROVED") {
          processedActivities.push({
            ...activity,
            displayType: "leave_approved",
            displayMessage: `${employee_name}'s ${leaveType} leave approved (${days} days)`,
            icon: "leave",
            color: "green",
            time: timestamp,
            badge: "Leave Approved",
            badgeColor: "green"
          });
        } else if (leaveStatus === "PENDING") {
          processedActivities.push({
            ...activity,
            displayType: "leave_pending",
            displayMessage: `${employee_name} requested ${leaveType} leave`,
            icon: "leave",
            color: "orange",
            time: timestamp,
            badge: "Pending Approval",
            badgeColor: "orange"
          });
        }
      }
      // Handle Salary Activities
      else if (type === "salary") {
        const netSalary = activity.meta?.net_salary || 0;
        const month = activity.meta?.month || "";
        const year = activity.meta?.year || "";

        processedActivities.push({
          ...activity,
          displayType: "salary",
          displayMessage: `Salary processed for ${employee_name}: ₹${netSalary} for ${month} ${year}`,
          icon: "salary",
          color: "purple",
          time: timestamp,
          badge: "Salary Processed",
          badgeColor: "purple"
        });
      }

      else if (type === "task") {
        const taskStatus = activity.meta?.status || "";
        // "created" (by the task's creator) vs "assigned" (to an assignee).
        // Defaults to "created" for older rows without the flag.
        const kind = activity.meta?.activity_kind || "created";
        const taskTitle = activity.title || "Task";

        if (taskStatus === "DONE") {
          processedActivities.push({
            ...activity,
            displayType: "task_completed",
            displayMessage: `${employee_name} completed task: ${taskTitle}`,
            icon: "check_square",
            color: "green",
            time: activity.timestamp,
            badge: "Task Completed",
            badgeColor: "green"
          });
        }
        else if (kind === "assigned") {
          // Someone was given a task → "<assignee> has a task: <title>".
          processedActivities.push({
            ...activity,
            displayType: "task_assigned",
            displayMessage: `${employee_name} has a task: ${taskTitle}`,
            icon: "check_square",
            color: "orange",
            time: activity.timestamp,
            badge: "Task Assigned",
            badgeColor: "orange"
          });
        }
        else {
          // The creator made the task → "<creator> has created a task: <title>".
          processedActivities.push({
            ...activity,
            displayType: "task_created",
            displayMessage: `${employee_name} has created a task: ${taskTitle}`,
            icon: "check_square",
            color: "orange",
            time: activity.timestamp,
            badge: "Task Created",
            badgeColor: "orange"
          });
        }
      }
    }

    // Sort by time (most recent first)
    processedActivities.sort((a, b) => {
      const timeA = a.time ? new Date(a.time).getTime() : 0;
      const timeB = b.time ? new Date(b.time).getTime() : 0;
      return timeB - timeA;
    });

    // Newest activity on top — no shuffle (chronological order is what users expect).
    return processedActivities;
  };

  // Recent activities advance ONLY on manual navigation (Prev / Next / dots) —
  // no auto-rotation. See the Prev/Next controls in the Recent activities card.
  const goToActivityPage = useCallback((pageIndex: number) => {
    const size = 4;
    const totalPages = Math.max(1, Math.ceil(activities.length / size));
    const target = ((pageIndex % totalPages) + totalPages) % totalPages; // wrap both ways
    setDisplayActivities(activities.slice(target * size, target * size + size));
    setCurrentPageIndex(target);
  }, [activities]);

  useEffect(() => {
    fetchData();
  }, []);

  // Weekly chart data. Everyone except admin sees their OWN working hours per day
  // ("Weekly Productivity Hours"); admin keeps the org-wide presence headcount
  // ("Daily presence"). Keyed on role because `user` hydrates from storage after
  // the first render, so role is undefined on the initial mount.
  useEffect(() => {
    const role = (user?.role || "").toLowerCase();
    const uid = (user as any)?.user_id || (user as any)?.id;
    const personalHoursView = role !== "admin";

    // Local (NOT UTC) calendar-date key — toISOString() is UTC and can shift a
    // bar's date by a day in IST, drifting the weekday label from its data.
    const fmtLocal = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const today = new Date();
    const dow = today.getDay(); // 0=Sun … 6=Sat
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dow === 0 ? 6 : dow - 1));
    const weekDates = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d;
    });
    const todayKey = fmtLocal(today);

    // Header label, e.g. "Jun 22 – 28" or "Jun 29 – Jul 5".
    const ws = weekDates[0], we = weekDates[6];
    const sameMonth = ws.getMonth() === we.getMonth();
    const startLbl = ws.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const endLbl = we.toLocaleDateString("en-US", sameMonth ? { day: "numeric" } : { month: "short", day: "numeric" });
    setAttendanceWeekLabel(`${startLbl} – ${endLbl}`);

    const buildBuckets = (
      valueFor: (d: Date, i: number) => number,
      statusFor?: (d: Date, i: number) => "present" | "absent" | "leave" | "none",
      activeFor?: (d: Date, i: number) => boolean,
    ) =>
      weekDates.map((d, i) => ({
        label: d.toLocaleDateString("en-US", { weekday: "short" }),
        dateNum: d.getDate(),
        value: valueFor(d, i),
        isToday: fmtLocal(d) === todayKey,
        status: statusFor ? statusFor(d, i) : undefined,
        isActive: activeFor ? activeFor(d, i) : false,
      }));

    let cancelled = false;
    const load = async () => {
      try {
        if (personalHoursView && uid) {
          // Per-day working hours + status for the signed-in employee (Mon → Sun).
          const [rows, settingsRes] = await Promise.all([
            getAttendanceDayWise(uid, fmtLocal(weekDates[0]), fmtLocal(weekDates[6])),
            salaryApi.getSettings().catch(() => null),
          ]);
          const hoursByDate = new Map<string, number>();
          const statusByDate = new Map<string, "present" | "leave" | "absent">();
          // "Currently active" = clocked in with no closing clock-out yet (an
          // open session). The backend leaves out_time null while the employee
          // is still working, so this flags the live/in-progress day.
          const activeByDate = new Map<string, boolean>();
          (Array.isArray(rows) ? rows : []).forEach((r: any) => {
            if (!r?.date) return;
            hoursByDate.set(String(r.date), Number(r.total_hours) || 0);
            const present = !!(r.In_time && r.In_time !== "00:00:00");
            statusByDate.set(String(r.date), present ? "present" : r.on_leave ? "leave" : "absent");
            const clockedOut = !!(r.out_time && r.out_time !== "00:00:00");
            activeByDate.set(String(r.date), present && !clockedOut);
          });
          const holidays = new Set<string>();
          const hd = (settingsRes as any)?.data?.data?.holiday_dates || [];
          (Array.isArray(hd) ? hd : []).forEach((h: any) => { if (h?.date) holidays.add(String(h.date)); });
          // Mirror the Monthly Presence rules: future days, weekends (Sat/Sun)
          // and holidays stay neutral so they aren't flagged red as "absent".
          const statusFor = (d: Date): "present" | "absent" | "leave" | "none" => {
            const ds = fmtLocal(d);
            if (ds > todayKey) return "none";
            const raw = statusByDate.get(ds);
            if (raw === "present") return "present";
            if (raw === "leave") return "leave";
            const dow = d.getDay();
            if (holidays.has(ds) || dow === 0 || dow === 6) return "none";
            return "absent";
          };
          // Only TODAY can be "active now"; a past open session is a forgotten
          // clock-out, not someone currently working.
          const activeFor = (d: Date): boolean =>
            fmtLocal(d) === todayKey && !!activeByDate.get(fmtLocal(d));
          if (!cancelled) setAttendanceTrend(buildBuckets((d) => hoursByDate.get(fmtLocal(d)) || 0, statusFor, activeFor));
        } else {
          // Org-wide present-headcount per day (distinct present users).
          const days = await Promise.all(weekDates.map((d) => getAttendanceByDate(fmtLocal(d))));
          if (!cancelled) setAttendanceTrend(buildBuckets((_, i) => countPresentForDay(Array.isArray(days[i]) ? days[i] : [])));
        }
      } catch {
        if (!cancelled) setAttendanceTrend(buildBuckets(() => 0));
      }
    };
    load();
    // Live refresh: while an employee is clocked in, today's hours grow with
    // the clock (the backend counts elapsed time to "now" for an open session).
    // Re-fetch every 60s so the bar keeps up without a manual reload. Only the
    // personal-hours view needs this; the org headcount view is not time-based.
    const timer = personalHoursView && uid ? setInterval(load, 60_000) : null;
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [user?.role]);

  // Monthly Presence Calendar (employee side / every non-admin role). Builds a
  // day-by-day status map for the current month from the signed-in user's own
  // attendance: present (clock-in), leave (approved-leave overlay), or absent.
  // Weekends, branch holidays and future days stay neutral so they aren't shown
  // as absences. Keyed on role for the same hydration reason as the chart above.
  useEffect(() => {
    if ((user?.role || "").toLowerCase() === "admin") { setPresenceMonth(null); return; }
    const uid = (user as any)?.user_id || (user as any)?.id;
    if (!uid) return;

    const fmtLocal = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const today = new Date();
    const y = today.getFullYear();
    const mo = today.getMonth();
    const todayNum = today.getDate();
    const daysInMonth = new Date(y, mo + 1, 0).getDate();
    const monthStartStr = fmtLocal(new Date(y, mo, 1));
    const todayStr = fmtLocal(today);
    const label = today.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    // Monday-first grid: how many blank cells precede the 1st.
    const leadBlanks = (new Date(y, mo, 1).getDay() + 6) % 7;

    let cancelled = false;
    (async () => {
      const statusByDate = new Map<string, "present" | "leave" | "absent">();
      const holidays = new Set<string>();
      try {
        const [rows, settingsRes] = await Promise.all([
          getAttendanceDayWise(uid, monthStartStr, todayStr),
          salaryApi.getSettings().catch(() => null),
        ]);
        (Array.isArray(rows) ? rows : []).forEach((r: any) => {
          if (!r?.date) return;
          const present = !!(r.In_time && r.In_time !== "00:00:00");
          statusByDate.set(String(r.date), present ? "present" : r.on_leave ? "leave" : "absent");
        });
        const hd = (settingsRes as any)?.data?.data?.holiday_dates || [];
        (Array.isArray(hd) ? hd : []).forEach((h: any) => { if (h?.date) holidays.add(String(h.date)); });
      } catch {
        // Leave the maps empty — every past day then renders neutral.
      }

      let present = 0, absent = 0, leave = 0;
      const days = Array.from({ length: daysInMonth }, (_, i) => {
        const n = i + 1;
        const d = new Date(y, mo, n);
        const ds = fmtLocal(d);
        const dow = d.getDay(); // 0=Sun … 6=Sat
        let status: "present" | "absent" | "leave" | "none";
        if (n > todayNum) {
          status = "none"; // future day
        } else {
          const raw = statusByDate.get(ds);
          if (raw === "present") status = "present";
          else if (raw === "leave") status = "leave";
          else if (holidays.has(ds) || dow === 0 || dow === 6) status = "none"; // holiday / weekend
          else status = "absent";
        }
        if (status === "present") present++;
        else if (status === "absent") absent++;
        else if (status === "leave") leave++;
        return { n, status, isToday: n === todayNum };
      });

      const denom = present + absent + leave;
      const pct = denom > 0 ? Math.round((present / denom) * 100) : 0;
      if (!cancelled) setPresenceMonth({ label, pct, leadBlanks, days });
    })();
    return () => { cancelled = true; };
  }, [user?.role]);

  // Employee task charts: Weekly Task Flow (per-day assigned vs completed this
  // week) + Monthly Productivity (created/assigned/reviewed this month). Both
  // come from the current user's own stats endpoints. Employee-only.
  useEffect(() => {
    if (!["employee", "intern"].includes((user?.role || "").toLowerCase())) return;
    const fmtLocal = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const todayKey = fmtLocal(new Date());
    let cancelled = false;
    (async () => {
      const [wfRes, mpRes] = await Promise.allSettled([
        tasksApi.getMyWeeklyFlow(),
        tasksApi.getMyMonthlyProductivity(),
      ]);
      if (cancelled) return;
      if (wfRes.status === "fulfilled") {
        const days: any[] = (wfRes.value as any)?.data?.days || [];
        setWeeklyFlow(days.map((d) => {
          const dt = new Date(`${d.date}T00:00:00`);
          return {
            label: dt.toLocaleDateString("en-US", { weekday: "short" }),
            dateNum: dt.getDate(),
            isToday: d.date === todayKey,
            assigned: Number(d.assigned) || 0,
            completed: Number(d.completed) || 0,
          };
        }));
        if (days.length) {
          const ws = new Date(`${days[0].date}T00:00:00`);
          const we = new Date(`${days[days.length - 1].date}T00:00:00`);
          const sameMonth = ws.getMonth() === we.getMonth();
          const startLbl = ws.toLocaleDateString("en-US", { month: "short", day: "numeric" });
          const endLbl = we.toLocaleDateString("en-US", sameMonth ? { day: "numeric" } : { month: "short", day: "numeric" });
          setWeeklyFlowLabel(`${startLbl} – ${endLbl}`);
        }
      }
      if (mpRes.status === "fulfilled") {
        const mp: any = (mpRes.value as any)?.data;
        if (mp) setMonthlyProd({
          total: Number(mp.total) || 0,
          todo: Number(mp.todo) || 0,
          in_progress: Number(mp.in_progress) || 0,
          completed: Number(mp.completed) || 0,
          reviewed: Number(mp.reviewed) || 0,
          productivity: Number(mp.productivity) || 0,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [user?.role]);

  // Org admin task charts: Weekly Task Flow (tasks CREATED vs COMPLETED this
  // week, org-wide) + Monthly Productivity (org this month). Both come from the
  // org stats endpoints (scoped server-side by visible_task_where). Admin only.
  useEffect(() => {
    if ((user?.role || "").toLowerCase() !== "admin") return;
    const fmtLocal = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const todayKey = fmtLocal(new Date());
    let cancelled = false;
    (async () => {
      const [wfRes, mpRes] = await Promise.allSettled([
        tasksApi.getOrgWeeklyFlow(),
        tasksApi.getOrgMonthlyProductivity(),
      ]);
      if (cancelled) return;
      if (wfRes.status === "fulfilled") {
        const days: any[] = (wfRes.value as any)?.data?.days || [];
        setOrgWeeklyFlow(days.map((d) => {
          const dt = new Date(`${d.date}T00:00:00`);
          return {
            label: dt.toLocaleDateString("en-US", { weekday: "short" }),
            dateNum: dt.getDate(),
            isToday: d.date === todayKey,
            // `assigned` slot carries the CREATED count for GroupedBarChart.
            assigned: Number(d.created) || 0,
            completed: Number(d.completed) || 0,
          };
        }));
        if (days.length) {
          const ws = new Date(`${days[0].date}T00:00:00`);
          const we = new Date(`${days[days.length - 1].date}T00:00:00`);
          const sameMonth = ws.getMonth() === we.getMonth();
          const startLbl = ws.toLocaleDateString("en-US", { month: "short", day: "numeric" });
          const endLbl = we.toLocaleDateString("en-US", sameMonth ? { day: "numeric" } : { month: "short", day: "numeric" });
          setOrgWeeklyFlowLabel(`${startLbl} – ${endLbl}`);
        }
      }
      if (mpRes.status === "fulfilled") {
        const mp: any = (mpRes.value as any)?.data;
        if (mp) setOrgMonthlyProd({
          total: Number(mp.total) || 0,
          todo: Number(mp.todo) || 0,
          in_progress: Number(mp.in_progress) || 0,
          completed: Number(mp.completed) || 0,
          reviewed: Number(mp.reviewed) || 0,
          productivity: Number(mp.productivity) || 0,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [user?.role]);

  // Admin Monthly Presence Calendar: colour each day of the current month by the
  // % of employees present (green ≥90, yellow ≥75, orange ≥50, red <50). Future
  // days, weekends and branch holidays stay neutral (no presence is expected, so
  // they aren't shown red). One org call gives every day's present headcount.
  useEffect(() => {
    if ((user?.role || "").toLowerCase() !== "admin") { setOrgPresenceMonth(null); return; }
    const fmtLocal = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const today = new Date();
    const y = today.getFullYear();
    const mo = today.getMonth();
    const todayNum = today.getDate();
    const daysInMonth = new Date(y, mo + 1, 0).getDate();
    const monthStartStr = fmtLocal(new Date(y, mo, 1));
    const todayStr = fmtLocal(today);
    const label = today.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    const leadBlanks = (new Date(y, mo, 1).getDay() + 6) % 7; // Monday-first grid

    let cancelled = false;
    (async () => {
      const pctByDate = new Map<string, number>();
      const holidays = new Set<string>();
      try {
        const [presence, settingsRes] = await Promise.all([
          getDailyPresence(monthStartStr, todayStr),
          salaryApi.getSettings().catch(() => null),
        ]);
        (presence?.days || []).forEach((d) => pctByDate.set(String(d.date), Number(d.percent) || 0));
        const hd = (settingsRes as any)?.data?.data?.holiday_dates || [];
        (Array.isArray(hd) ? hd : []).forEach((h: any) => { if (h?.date) holidays.add(String(h.date)); });
      } catch {
        // Leave the maps empty — every past weekday then renders red (0%).
      }

      let sumPct = 0, coloredCount = 0;
      const days = Array.from({ length: daysInMonth }, (_, i) => {
        const n = i + 1;
        const d = new Date(y, mo, n);
        const ds = fmtLocal(d);
        const dow = d.getDay(); // 0=Sun … 6=Sat
        let colored = true;
        let percent = pctByDate.get(ds) ?? 0;
        // Future days, weekends and holidays carry no presence expectation.
        if (n > todayNum || holidays.has(ds) || dow === 0 || dow === 6) {
          colored = false;
          percent = 0;
        }
        if (colored) { sumPct += percent; coloredCount++; }
        return { n, percent, colored, isToday: n === todayNum };
      });
      const avgPct = coloredCount > 0 ? Math.round(sumPct / coloredCount) : 0;
      if (!cancelled) setOrgPresenceMonth({ label, avgPct, leadBlanks, days });
    })();
    return () => { cancelled = true; };
  }, [user?.role]);

  // Load the next upcoming holiday from branch salary settings.
  // Shown for employee / intern / supervisor / manager / hr / hr head / hod / team lead.
  // Top-level admin/founder/director/owner/superadmin don't get the tile.
  useEffect(() => {
    const role = (user?.role || "").toLowerCase();
    const HOLIDAY_TILE_ROLES = new Set([
      // Intern is the employee tier, so it gets the same self-service tiles.
      "employee", "intern", "supervisor", "manager", "hr", "hr head", "hr admin", "hod", "team lead",
    ]);
    if (!HOLIDAY_TILE_ROLES.has(role)) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await salaryApi.getSettings();
        const list: { date?: string; name?: string }[] = res?.data?.data?.holiday_dates || [];
        const today = new Date().toISOString().split("T")[0];
        const upcoming = list
          .filter((h) => h && typeof h.date === "string" && h.date >= today)
          .sort((a, b) => (a.date! < b.date! ? -1 : 1));
        if (!cancelled && upcoming.length > 0) {
          setNextHoliday({ date: upcoming[0].date!, name: upcoming[0].name || "Holiday" });
        }
      } catch {
        // Silent — holiday tile just falls back to "—" if the fetch fails.
      }
    })();
    return () => { cancelled = true; };
  }, [user?.role]);

  // Refresh data every 30 seconds
  // useEffect(() => {
  //   const refreshInterval = setInterval(() => {
  //     fetchData();
  //   }, 30000);

  //   return () => clearInterval(refreshInterval);
  // }, []);

  if (loading && activities.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const getWelcomeMessage = () => {
    // const hour = new Date().getHours();
    // if (hour < 12) return "Hey";
    // if (hour < 18) return "Hey";
    return "Hey";
  };

  const getStatsForRole = () => {
    const baseStats = [
      {
        title: "Total Employees",
        value: stats?.total_employees || 0,
        icon: Users,
        tone: "info" as const,
        description: `${stats?.activeEmployees || 0} active`,
      },
      {
        title: "Pending Leaves",
        value: stats?.pending_leaves || 0,
        icon: Calendar,
        tone: "warning" as const,
        description: "Requires approval",
      },
      {
        title: "Today's Attendance",
        value: `${stats?.todays_present || 0}`,
        icon: Clock,
        tone: "success" as const,
        description: "Present today",
      },
      {
        title: "Active Tasks",
        value: stats?.openTasks || 0,
        icon: CheckSquare,
        tone: "primary" as const,
        description: `${stats?.completedTasks || 0} completed`,
      },
    ];
    if (isEmployee) {
      return baseStats.slice(1, 4);
    }
    return baseStats;
  };

  // Tone palette for premium stat tiles (matches Task Reports aesthetic)
  const STAT_TONES: Record<string, {
    iconBg: string;
    iconFg: string;
    valueColor?: string;
  }> = {
    info:    { iconBg: 'var(--gt-prio-low-bg)',  iconFg: 'var(--gt-prio-low-fg)' },
    warning: { iconBg: 'var(--gt-prio-med-bg)',  iconFg: 'var(--gt-prio-med-fg)' },
    success: { iconBg: 'var(--gt-status-done-bg)', iconFg: 'var(--gt-status-done-fg)', valueColor: 'var(--gt-accent-strong)' },
    danger:  { iconBg: 'var(--gt-prio-urgent-bg)', iconFg: 'var(--gt-prio-urgent-fg)', valueColor: '#7f1d1d' },
    primary: { iconBg: 'var(--gt-primary-container)', iconFg: 'var(--gt-primary-deep)' },
  };

  const getActivityIconComponent = (iconType: string) => {
    switch (iconType) {
      case "clock_in":
        return <LogIn className="h-4 w-4" />;
      case "clock_out":
        return <LogOut className="h-4 w-4" />;
      case "leave":
        return <Calendar className="h-4 w-4" />;
      case "employee":
        return <UserPlus className="h-4 w-4" />;
      case "salary":
        return <IndianRupee className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getActivityColorClass = (color: string) => {
    switch (color) {
      case "green":
        return "bg-green-100 text-green-600";
      case "red":
        return "bg-red-100 text-red-600";
      case "orange":
        return "bg-orange-100 text-orange-600";
      case "blue":
        return "bg-blue-100 text-blue-600";
      case "purple":
        return "bg-purple-100 text-purple-600";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const getBadgeColorClass = (badgeColor: string) => {
    switch (badgeColor) {
      case "green":
        return "bg-green-50 text-green-600 border-green-200";
      case "red":
        return "bg-red-50 text-red-600 border-red-200";
      case "orange":
        return "bg-orange-50 text-orange-600 border-orange-200";
      case "purple":
        return "bg-purple-50 text-purple-600 border-purple-200";
      default:
        return "bg-gray-50 text-gray-600 border-gray-200";
    }
  };

  const getTimeAgo = (timestamp: string) => {
    if (!timestamp) return "Just now";
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return "Recently";
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins} min ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      return date.toLocaleDateString();
    } catch {
      return "Recently";
    }
  };

  const currentDisplay = displayActivities;

  return (
    <div className="gt-shell min-h-screen relative" style={{ background: "var(--gt-surface-1)" }}>
      <style>{`
        @keyframes dash-fade-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes dash-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes dash-shine { from { transform: translateX(-100%); } to { transform: translateX(220%); } }
        .dash-fade { animation: dash-fade-up 0.85s cubic-bezier(0.22, 1, 0.36, 1) backwards; }
        .dash-pulse { animation: dash-pulse 1.6s ease-in-out infinite; }
        .dash-reveal { opacity: 0; transform: translateY(24px); transition: opacity 900ms cubic-bezier(0.22, 1, 0.36, 1), transform 900ms cubic-bezier(0.22, 1, 0.36, 1); }
        .dash-reveal.is-revealed { opacity: 1; transform: translateY(0); }
        .dash-stagger > * { opacity: 0; transform: translateY(18px); transition: opacity 700ms cubic-bezier(0.22, 1, 0.36, 1), transform 700ms cubic-bezier(0.22, 1, 0.36, 1); }
        .dash-stagger.is-revealed > * { opacity: 1; transform: translateY(0); }
        .dash-stagger.is-revealed > *:nth-child(1) { transition-delay: 0ms; }
        .dash-stagger.is-revealed > *:nth-child(2) { transition-delay: 70ms; }
        .dash-stagger.is-revealed > *:nth-child(3) { transition-delay: 140ms; }
        .dash-stagger.is-revealed > *:nth-child(4) { transition-delay: 210ms; }
        .dash-stagger.is-revealed > *:nth-child(5) { transition-delay: 280ms; }
        .dash-stagger.is-revealed > *:nth-child(6) { transition-delay: 350ms; }
        .dash-stagger.is-revealed > *:nth-child(7) { transition-delay: 420ms; }
        .dash-stagger.is-revealed > *:nth-child(8) { transition-delay: 490ms; }
        .dash-stagger.is-revealed > *:nth-child(9) { transition-delay: 560ms; }
        .dash-tile { transition: transform 360ms cubic-bezier(0.33, 1, 0.68, 1), box-shadow 360ms, border-color 240ms; cursor: pointer; }
        .dash-tile:hover { transform: translateY(-4px); border-color: var(--gt-primary) !important; box-shadow: 0 24px 48px -16px rgba(15,45,31,0.16), 0 4px 10px -4px rgba(15,45,31,0.06); }
        .dash-arrow { transition: transform 240ms cubic-bezier(0.33, 1, 0.68, 1); }
        .dash-tile:hover .dash-arrow { transform: translateX(4px); }
        /* Every insights chart tile shares one height so the grid reads as an
           even matrix — rows line up regardless of which cards a role/access
           profile toggles on. Charts that fill less just centre in the tile. */
        @media (min-width: 1024px) { .dash-insights > * { min-height: 360px; } }
        .dash-shimmer { position: relative; overflow: hidden; }
        .dash-shimmer::after { content: ""; position: absolute; inset: 0; background: linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.18) 50%, transparent 70%); animation: dash-shine 2.6s cubic-bezier(0.4, 0, 0.2, 1) infinite; pointer-events: none; }
        .dash-eyebrow { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10.5px; letter-spacing: 0.22em; text-transform: uppercase; font-weight: 500; color: var(--gt-text-tertiary); }
        .dash-serif { font-family: 'Fraunces', 'Iowan Old Style', Georgia, serif !important; }
        .dash-mono { font-family: 'JetBrains Mono', ui-monospace, monospace !important; }
        .dash-dotted { background-image: radial-gradient(circle, currentColor 1px, transparent 1.2px); background-size: 8px 8px; background-repeat: repeat-x; }
        .dash-ledger { display: grid; grid-template-columns: repeat(var(--ledger-cols, 4), 1fr); }
        .dash-ledger > .dash-ledger-cell { border-left: 1px solid var(--gt-divider); transition: background 280ms ease; }
        .dash-ledger > .dash-ledger-cell:first-child { border-left: none; }
        .dash-ledger > .dash-ledger-cell:hover { background: var(--gt-surface-1); }
        @media (max-width: 1023px) {
          .dash-ledger { grid-template-columns: repeat(2, 1fr); }
          .dash-ledger > .dash-ledger-cell:nth-child(odd) { border-left: none; }
          .dash-ledger > .dash-ledger-cell:nth-child(n+3) { border-top: 1px solid var(--gt-divider); }
        }
        @media (prefers-reduced-motion: reduce) {
          .dash-fade, .dash-pulse, .dash-shimmer::after, .dash-reveal, .dash-stagger > * {
            animation: none !important;
            transition: none !important;
            opacity: 1 !important;
            transform: none !important;
          }
        }
      `}</style>

      {/* DashRevealRunner — wraps a one-time IntersectionObserver call */}
      <DashRevealRunner />

      {/* Flex column so sections can be re-ordered with CSS `order` (DOM order preserved).
          Visual order: Welcome → Ledger → Insights → Recent activities → Modules → Health. */}
      <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-7 dash-fade" style={{ display: "flex", flexDirection: "column" }}>
        {/* WELCOME — editorial */}
        <div className="dash-reveal flex flex-col md:flex-row gap-5 items-start md:items-end justify-between" style={{ order: -4 }}>
          <div>
            <h1
              className="dash-serif"
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontSize: "clamp(34px, 4vw, 52px)",
                lineHeight: 1.0,
                letterSpacing: "-0.025em",
                fontWeight: 500,
                color: "var(--gt-text-primary)",
              }}
            >
              {getWelcomeMessage()},{" "}
              <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>
                {user?.name?.split(" ")[0] || "User"}
              </span>
            </h1>
            <p
              style={{
                marginTop: 10,
                fontSize: 15,
                color: "var(--gt-text-secondary)",
                maxWidth: 560,
                lineHeight: 1.55,
              }}
            >
              Here's what's happening with your{" "}
              <em className="dash-serif" style={{ fontStyle: "italic" }}>
                {user?.role === "admin" ? "organization" : "work"}
              </em>{" "}
              today.
            </p>
          </div>

          <div className="flex items-stretch gap-3 flex-wrap md:flex-nowrap">
            {/* UPCOMING HOLIDAY — calendar-style tile, sits to the left of
                today's date. Shown to every role (the ledger tile was removed),
                so employees see it too. */}
            {nextHoliday && (() => {
              const hd = new Date(nextHoliday.date + "T00:00:00");
              const valid = !Number.isNaN(hd.getTime());
              const mon = valid ? hd.toLocaleDateString("en-US", { month: "short" }).toUpperCase() : "";
              const day = valid ? hd.getDate() : "";
              const long = valid
                ? hd.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })
                : nextHoliday.date;
              return (
                <button
                  onClick={() => navigate("/leaves")}
                  className="flex items-center gap-3.5 rounded-2xl px-4 py-3 text-left"
                  style={{
                    background: "var(--gt-surface-0)",
                    border: "1px solid var(--gt-border)",
                    boxShadow: "var(--gt-elev-1)",
                    cursor: "pointer",
                  }}
                  title={`${nextHoliday.name} · ${long}`}
                >
                  {/* mini calendar glyph */}
                  <div className="rounded-xl overflow-hidden shrink-0" style={{ width: 48, border: "1px solid var(--gt-border)" }}>
                    <div
                      style={{
                        background: "var(--gt-prio-urgent-bg)",
                        color: "var(--gt-prio-urgent-fg)",
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: "0.14em",
                        textAlign: "center",
                        padding: "3px 0",
                      }}
                    >
                      {mon}
                    </div>
                    <div
                      className="dash-serif"
                      style={{
                        fontFamily: "'Fraunces', Georgia, serif",
                        fontSize: 22,
                        fontWeight: 500,
                        textAlign: "center",
                        padding: "3px 0 5px",
                        lineHeight: 1,
                        color: "var(--gt-text-primary)",
                        background: "var(--gt-surface-0)",
                      }}
                    >
                      {day}
                    </div>
                  </div>
                  <div>
                    <div className="dash-eyebrow" style={{ marginBottom: 4, fontSize: 9.5 }}>
                      Upcoming Holiday
                    </div>
                    <div
                      className="dash-serif"
                      style={{
                        fontFamily: "'Fraunces', Georgia, serif",
                        fontSize: 16,
                        letterSpacing: "-0.01em",
                        fontWeight: 500,
                        color: "var(--gt-text-primary)",
                        lineHeight: 1.15,
                      }}
                    >
                      {nextHoliday.name}
                    </div>
                  </div>
                </button>
              );
            })()}

            {/* TODAY'S DATE */}
            <div
              className="hidden md:flex items-center gap-4 rounded-2xl px-5 py-4"
              style={{
                background: "var(--gt-surface-0)",
                border: "1px solid var(--gt-border)",
                boxShadow: "var(--gt-elev-1)",
              }}
            >
              <div>
                <div className="dash-eyebrow" style={{ marginBottom: 4, fontSize: 9.5 }}>
                  Today's date
                </div>
                <div
                  className="dash-serif"
                  style={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontSize: 18,
                    letterSpacing: "-0.01em",
                    fontWeight: 500,
                    color: "var(--gt-text-primary)",
                  }}
                >
                  {new Date().toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
              </div>
              <div className="dash-pulse w-2 h-2 rounded-full" style={{ background: "var(--gt-online)" }} />
            </div>
          </div>
        </div>

        {/* HERO LEDGER — signature metrics masthead. Hidden on the focused
            employee dashboard (greeting + charts + modules only). */}
        {!isEmployee && (
        <div
          className="dash-reveal dash-stagger dash-ledger"
          style={{
            order: -3,
            background: "var(--gt-surface-0)",
            border: "1px solid var(--gt-border)",
            borderRadius: 22,
            boxShadow: "var(--gt-elev-3)",
            overflow: "hidden",
            ["--ledger-cols" as any]: ledgerCols,
          }}
        >
          {(() => {
            const all: Array<{ title: string; value: number | string; sub: string; Icon: any; href: string; tone: string; isDate?: boolean }> = [
              { title: "Total Employees", value: stats?.total_employees || 0, sub: `${stats?.activeEmployees || 0} active`, Icon: Users, href: "/employees", tone: "primary" },
              { title: "Pending Leaves", value: stats?.pending_leaves || 0, sub: "Requires approval", Icon: Calendar, href: "/leaves", tone: "warning" },
              { title: "Today's Attendance", value: stats?.todays_present || 0, sub: "Present today", Icon: Clock, href: "/attendance", tone: "success" },
              { title: "Active Tasks", value: stats?.openTasks || 0, sub: `${stats?.completedTasks || 0} completed`, Icon: CheckSquare, href: "/tasks/list", tone: "info" },
            ];
            // Non-elite roles (employee / supervisor / manager / hr / hr head / hod / team lead)
            // drop the org-wide "Total Employees" tile and show their three personal
            // metrics. The Upcoming Holiday now lives in the header calendar widget.
            const list = dropsHeadcountTile ? all.slice(1, 4) : all;
            const TONE_BG: Record<string, string> = {
              primary: "var(--gt-primary-container)",
              warning: "var(--gt-prio-med-bg)",
              success: "var(--gt-status-done-bg)",
              info: "var(--gt-prio-low-bg)",
              holiday: "var(--gt-prio-urgent-bg)",
            };
            const TONE_FG: Record<string, string> = {
              primary: "var(--gt-primary-deep)",
              warning: "var(--gt-prio-med-fg)",
              success: "var(--gt-status-done-fg)",
              info: "var(--gt-prio-low-fg)",
              holiday: "var(--gt-prio-urgent-fg)",
            };
            return list.map((tile, i) => (
              <button
                key={i}
                onClick={() => navigate(tile.href)}
                className="dash-ledger-cell text-left relative"
                style={{ padding: "22px 24px", cursor: "pointer", background: "transparent", border: "none" }}
              >
                <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
                  <span className="dash-eyebrow" style={{ fontSize: 10 }}>
                    {tile.title}
                  </span>
                  <span
                    className="w-8 h-8 rounded-xl flex items-center justify-center"
                    style={{ background: TONE_BG[tile.tone], color: TONE_FG[tile.tone] }}
                  >
                    <tile.Icon className="w-4 h-4" />
                  </span>
                </div>
                <div
                  className="dash-serif"
                  style={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    // Big editorial numeral; the date tile is shorter so it doesn't wrap.
                    fontSize: tile.isDate ? 30 : 46,
                    fontWeight: 500,
                    letterSpacing: "-0.03em",
                    lineHeight: 0.95,
                    color: "var(--gt-text-primary)",
                  }}
                >
                  {tile.isDate
                    ? <span>{tile.value}</span>
                    : <Counter value={Number(tile.value) || 0} />}
                </div>
                <div
                  className="dash-serif flex items-center gap-1.5"
                  style={{ marginTop: 10, fontSize: 12.5, color: "var(--gt-text-tertiary)", fontStyle: "italic" }}
                >
                  <span>{tile.sub}</span>
                  <ArrowRight className="dash-arrow w-3 h-3 ml-auto" style={{ color: "var(--gt-primary)" }} />
                </div>
              </button>
            ));
          })()}
        </div>
        )}

        {/* INSIGHTS — charts & graphs across modules */}
        <div style={{ order: -2 }}>
          <div className="dash-reveal flex items-end justify-between mb-5 flex-wrap gap-3">
            <div>
              <h2
                className="dash-serif"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: "clamp(24px, 2.6vw, 34px)",
                  lineHeight: 1.05,
                  letterSpacing: "-0.02em",
                  fontWeight: 500,
                  color: "var(--gt-text-primary)",
                }}
              >
                A live <span style={{ fontStyle: "italic" }}>read</span> of every module
              </h2>
            </div>
            <span className="inline-flex items-center gap-1.5 dash-mono" style={{ fontSize: 10, color: "var(--gt-online)", letterSpacing: "0.2em", textTransform: "uppercase" }}>
              <span className="dash-pulse w-1.5 h-1.5 rounded-full" style={{ background: "var(--gt-online)" }} />
              Live data
            </span>
          </div>

          <div className="dash-reveal dash-stagger dash-insights grid grid-cols-1 lg:grid-cols-6 gap-4">
            {/* 7-day attendance trend (wide) */}
            {cardVisible("dash-weekly-productivity") && (
            <button
              data-chart="weekly-productivity-hours"
              onClick={cardNav("dash-weekly-productivity", "/attendance")}
              className="dash-tile text-left rounded-2xl p-6 lg:col-span-3 flex flex-col items-stretch"
              style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="dash-eyebrow mb-1.5">{isPersonalHoursView ? "Productivity" : "Attendance"} · {attendanceWeekLabel || "this week"}</div>
                  <h3
                    className="dash-serif"
                    style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 500, letterSpacing: "-0.015em", color: "var(--gt-text-primary)" }}
                  >
                    {isPersonalHoursView ? (
                      <>Weekly Productivity <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>Hours</span></>
                    ) : (
                      <>Daily <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>presence</span></>
                    )}
                  </h3>
                </div>
                <div className="text-right">
                  <div className="dash-serif" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 28, fontWeight: 500, letterSpacing: "-0.02em", color: "var(--gt-text-primary)", lineHeight: 1 }}>
                    <Counter value={attendanceTrend.reduce((s, d) => s + d.value, 0)} suffix={isPersonalHoursView ? "h" : ""} />
                  </div>
                  <div className="dash-eyebrow" style={{ fontSize: 9.5, marginTop: 4 }}>total this week</div>
                </div>
              </div>
              <div className="flex-1 flex flex-col justify-center">
                {/* Always render the chart — days with no data show as empty
                    bars rather than a blank "no data" message. */}
                <BarChart
                  data={attendanceTrend}
                  height={150}
                  valueFormat={isPersonalHoursView ? (v) => (v > 0 ? `${+v.toFixed(1)}h` : "0") : undefined}
                />
                {isPersonalHoursView && attendanceTrend.some((d) => d.status === "present" || d.status === "absent" || d.status === "leave") && (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2.5" style={{ fontSize: 11, color: "var(--gt-text-secondary)" }}>
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "var(--gt-primary)" }} /> Present</span>
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#dc2626" }} /> Absent</span>
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#f59e0b" }} /> Leave</span>
                  </div>
                )}
              </div>
            </button>
            )}

            {/* Monthly Presence Calendar — ORG side (admin). Each day is coloured
                by the % of employees present; the header % is the month average. */}
            {cardVisible("dash-monthly-presence") && isAdmin && orgPresenceMonth && (
              <button
                data-chart="monthly-presence-calendar"
                onClick={cardNav("dash-monthly-presence", "/attendance")}
                className="dash-tile text-left rounded-2xl p-6 lg:col-span-3 flex flex-col items-stretch"
                style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="dash-eyebrow mb-1.5">Attendance · {orgPresenceMonth.label}</div>
                    <h3
                      className="dash-serif"
                      style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 500, letterSpacing: "-0.015em", color: "var(--gt-text-primary)" }}
                    >
                      Monthly Presence <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>Calendar</span>
                    </h3>
                  </div>
                  <div className="text-right">
                    <div className="dash-serif" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 28, fontWeight: 500, letterSpacing: "-0.02em", color: "var(--gt-text-primary)", lineHeight: 1 }}>
                      <Counter value={orgPresenceMonth.avgPct} suffix="%" />
                    </div>
                    <div className="dash-eyebrow" style={{ fontSize: 9.5, marginTop: 4 }}>avg presence</div>
                  </div>
                </div>
                <OrgMonthlyPresenceCalendar leadBlanks={orgPresenceMonth.leadBlanks} days={orgPresenceMonth.days} />
              </button>
            )}

            {/* Monthly Presence Calendar — employee side (non-admin) */}
            {cardVisible("dash-monthly-presence") && !isAdmin && presenceMonth && (
              <button
                data-chart="monthly-presence-calendar"
                onClick={cardNav("dash-monthly-presence", "/attendance")}
                className="dash-tile text-left rounded-2xl p-6 lg:col-span-3 flex flex-col items-stretch"
                style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="dash-eyebrow mb-1.5">Attendance · {presenceMonth.label}</div>
                    <h3
                      className="dash-serif"
                      style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 500, letterSpacing: "-0.015em", color: "var(--gt-text-primary)" }}
                    >
                      Monthly Presence <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>Calendar</span>
                    </h3>
                  </div>
                  <div className="text-right">
                    <div className="dash-serif" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 28, fontWeight: 500, letterSpacing: "-0.02em", color: "var(--gt-text-primary)", lineHeight: 1 }}>
                      <Counter value={presenceMonth.pct} suffix="%" />
                    </div>
                    <div className="dash-eyebrow" style={{ fontSize: 9.5, marginTop: 4 }}>presence</div>
                  </div>
                </div>
                <MonthlyPresenceCalendar leadBlanks={presenceMonth.leadBlanks} days={presenceMonth.days} />
              </button>
            )}

            {/* Weekly Task Flow — admin: org tasks CREATED vs COMPLETED per day;
                employee: own tasks assigned vs completed. `assigned` on the org
                data carries the CREATED count so both feed the same chart. */}
            {cardVisible("dash-weekly-task-flow") && (() => {
              const flowData = isAdmin ? orgWeeklyFlow : weeklyFlow;
              const flowLabel = isAdmin ? orgWeeklyFlowLabel : weeklyFlowLabel;
              const firstLabel = isAdmin ? "Created" : "Assigned";
              return (
              <button
                data-chart="weekly-task-flow"
                onClick={cardNav("dash-weekly-task-flow", isAdmin ? "/tasks/list" : "/tasks/my-tasks")}
                className="dash-tile text-left rounded-2xl p-6 lg:col-span-3 flex flex-col items-stretch"
                style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="dash-eyebrow mb-1.5">Tasks · {flowLabel || "this week"}</div>
                    <h3 className="dash-serif" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 500, letterSpacing: "-0.015em", color: "var(--gt-text-primary)" }}>
                      Weekly Task <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>Flow</span>
                    </h3>
                  </div>
                  <div className="flex flex-col gap-1.5 items-end" style={{ fontSize: 11, color: "var(--gt-text-secondary)" }}>
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "var(--gt-primary)" }} /> {firstLabel}</span>
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "var(--gt-online)" }} /> Completed</span>
                  </div>
                </div>
                <div className="flex-1 flex flex-col justify-center">
                  {/* Always render the chart — a quiet week shows empty bars
                      rather than a blank "no data" message. */}
                  <GroupedBarChart data={flowData} height={150} />
                </div>
              </button>
              );
            })()}

            {/* Project Execution Progress — % of tasks completed per project
                (DONE tasks / total tasks). Sits beside Weekly Task Flow. */}
            {cardVisible("dash-project-execution") && (
              <button
                data-chart="project-execution"
                onClick={cardNav("dash-project-execution", "/projects")}
                className="dash-tile text-left rounded-2xl p-6 lg:col-span-3 flex flex-col items-stretch"
                style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
              >
                {(() => {
                  const rows = (Array.isArray(projectsList) ? projectsList : [])
                    .map((p: any) => {
                      const tc = p.task_counts || {};
                      const total = Number(tc.total ?? 0);
                      const bs = tc.by_status || {};
                      // "Completed" = DONE tasks; CANCELLED isn't completed work.
                      const done = Number(bs.DONE ?? bs.done ?? 0);
                      const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                      return {
                        name: p.name || p.project_name || p.title || "Untitled project",
                        total,
                        done,
                        pct,
                      };
                    })
                    .filter((r) => r.total > 0)
                    .sort((a, b) => b.pct - a.pct || b.total - a.total)
                    .slice(0, 5);

                  const totalTasks = rows.reduce((s, r) => s + r.total, 0);
                  const doneTasks = rows.reduce((s, r) => s + r.done, 0);
                  const overall = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;

                  return (
                    <>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="dash-eyebrow mb-1.5">Projects · Completion</div>
                          <h3 className="dash-serif" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 500, letterSpacing: "-0.015em", color: "var(--gt-text-primary)" }}>
                            Project Execution <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>Progress</span>
                          </h3>
                        </div>
                        <div className="text-right">
                          <div className="dash-serif" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 28, fontWeight: 500, letterSpacing: "-0.02em", color: "var(--gt-text-primary)", lineHeight: 1 }}>
                            <Counter value={overall} suffix="%" />
                          </div>
                          <div className="dash-eyebrow" style={{ fontSize: 9.5, marginTop: 4 }}>overall</div>
                        </div>
                      </div>
                      <div className="flex-1 flex flex-col justify-center">
                        {/* With no projects/tasks, show empty 0% tracks rather
                            than a blank "no data" message. */}
                        <div className="space-y-3">
                          {(rows.length > 0
                            ? rows
                            : [{ name: "No active projects", total: 0, done: 0, pct: 0 }]
                          ).map((r, i) => (
                            <div key={i}>
                              <div className="flex items-center justify-between mb-1.5" style={{ fontSize: 12.5 }}>
                                <span className="truncate" style={{ fontWeight: 600, color: rows.length > 0 ? "var(--gt-text-primary)" : "var(--gt-text-tertiary)", maxWidth: "60%" }}>{r.name}</span>
                                <span className="dash-mono" style={{ color: "var(--gt-text-tertiary)", flexShrink: 0 }}>
                                  <span style={{ color: "var(--gt-text-primary)", fontWeight: 600 }}>{r.pct}%</span> · {r.done}/{r.total}
                                </span>
                              </div>
                              <div style={{ height: 8, borderRadius: 999, background: "var(--gt-surface-2)", overflow: "hidden" }}>
                                <div style={{ width: `${r.pct}%`, height: "100%", borderRadius: 999, background: "linear-gradient(90deg, var(--gt-primary-soft), var(--gt-primary))", transition: "width 1100ms cubic-bezier(0.22,1,0.36,1)" }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </button>
            )}

            {/* Monthly Productivity — pie shows productivity %
                (completed or reviewed / tasks created this month); side legend
                lists tasks by status. Admin = org-wide; employee = own tasks. */}
            {cardVisible("dash-monthly-productivity") && (() => {
              const mp = (isAdmin ? orgMonthlyProd : monthlyProd) || { total: 0, todo: 0, in_progress: 0, completed: 0, reviewed: 0, productivity: 0 };
              const productive = mp.completed + mp.reviewed;
              const remaining = Math.max(0, mp.total - productive);
              // Pie: the productive share vs the rest of this month's tasks.
              const pie: Segment[] = [
                { label: "Completed / Reviewed", value: productive, color: "#1d9e75" },
                { label: "Remaining", value: remaining, color: "var(--gt-border)" },
              ];
              // Side legend: status breakdown of the user's tasks this month.
              const legend: Segment[] = [
                { label: "To Do", value: mp.todo, color: "#378add" },
                { label: "In Progress", value: mp.in_progress, color: "#ef9f27" },
                { label: "Completed", value: mp.completed, color: "#1d9e75" },
              ];
              return (
                <button
                  data-chart="monthly-productivity"
                  onClick={cardNav("dash-monthly-productivity", "/tasks/list")}
                  className="dash-tile text-left rounded-2xl p-6 lg:col-span-3"
                  style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="dash-eyebrow mb-1.5">Tasks · Monthly productivity</div>
                      <h3 className="dash-serif" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 500, letterSpacing: "-0.015em", color: "var(--gt-text-primary)" }}>
                        Monthly <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>Productivity</span>
                      </h3>
                    </div>
                    <ArrowRight className="dash-arrow w-4 h-4" style={{ color: "var(--gt-primary)" }} />
                  </div>
                  {/* Always render the donut — with no tasks it shows 0% over an
                      empty ring rather than a blank "no data" message. */}
                  <div className="flex items-center gap-6">
                    <DonutChart segments={pie} size={150} thickness={18} centerValue={<Counter value={mp.productivity} suffix="%" />} centerLabel="productive" />
                    <div className="flex-1 space-y-2.5">
                      {legend.map((s, i) => (
                        <div key={i} className="flex items-center justify-between" style={{ fontSize: 12.5 }}>
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                            <span style={{ fontWeight: 600, color: "var(--gt-text-secondary)" }}>{s.label}</span>
                          </span>
                          <span className="dash-mono" style={{ color: "var(--gt-text-primary)", fontWeight: 600 }}>{s.value}</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between pt-2 mt-1" style={{ fontSize: 12.5, borderTop: "1px solid var(--gt-border)" }}>
                        <span style={{ fontWeight: 600, color: "var(--gt-text-secondary)" }}>Created this month</span>
                        <span className="dash-mono" style={{ color: "var(--gt-text-primary)", fontWeight: 600 }}>{mp.total}</span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })()}

            {/* Tasks by priority — donut. Admin only: on the employee side the
                Monthly Presence Calendar above takes this slot instead. */}
            {cardVisible("dash-tasks-priority") && (
            <button
              data-chart="tasks-by-priority"
              onClick={cardNav("dash-tasks-priority", "/tasks/list")}
              className="dash-tile text-left rounded-2xl p-6 lg:col-span-3"
              style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="dash-eyebrow mb-1.5">Tasks · By priority</div>
                  <h3
                    className="dash-serif"
                    style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 500, letterSpacing: "-0.015em", color: "var(--gt-text-primary)" }}
                  >
                    Where <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>focus</span> lives
                  </h3>
                </div>
                <ArrowRight className="dash-arrow w-4 h-4" style={{ color: "var(--gt-primary)" }} />
              </div>
              {(() => {
                const byP = (taskStats?.by_priority || {}) as Record<string, number>;
                const segs: Segment[] = [
                  { label: "Urgent", value: byP.URGENT || byP.urgent || 0, color: "var(--gt-prio-urgent-dot)" },
                  { label: "High", value: byP.HIGH || byP.high || 0, color: "var(--gt-prio-high-dot)" },
                  { label: "Medium", value: byP.MEDIUM || byP.medium || 0, color: "var(--gt-prio-med-dot)" },
                  { label: "Low", value: byP.LOW || byP.low || 0, color: "var(--gt-prio-low-dot)" },
                ];
                const total = segs.reduce((s, x) => s + x.value, 0);
                // Always render the donut — no open tasks shows 0 over an empty
                // ring rather than a blank "no data" message.
                return (
                  <div className="flex items-center gap-6">
                    <DonutChart
                      segments={segs}
                      size={150}
                      thickness={18}
                      centerValue={<Counter value={total} />}
                      centerLabel="open tasks"
                    />
                    <div className="flex-1 space-y-2.5">
                      {segs.map((s, i) => (
                        <div key={i} className="flex items-center justify-between" style={{ fontSize: 12.5 }}>
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                            <span style={{ fontWeight: 600, color: "var(--gt-text-secondary)" }}>{s.label}</span>
                          </span>
                          <span className="dash-mono" style={{ color: "var(--gt-text-tertiary)" }}>
                            <span style={{ color: "var(--gt-text-primary)", fontWeight: 600 }}>{s.value}</span> · {total > 0 ? Math.round((s.value / total) * 100) : 0}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </button>
            )}

            {/* Remaining insight tiles — each gated by its own dash-* access key. */}
            {/* Tasks by status — donut */}
            {cardVisible("dash-tasks-status") && (
            <button
              data-chart="tasks-by-status"
              onClick={cardNav("dash-tasks-status", "/tasks/list")}
              className="dash-tile text-left rounded-2xl p-6 lg:col-span-2"
              style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
            >
              <div className="dash-eyebrow mb-1.5">Tasks · Health</div>
              <h3
                className="dash-serif mb-4"
                style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 500, letterSpacing: "-0.015em", color: "var(--gt-text-primary)" }}
              >
                Status <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>mix</span>
              </h3>
              {(() => {
                const open = taskStats?.open ?? stats?.openTasks ?? 0;
                const overdue = taskStats?.overdue ?? 0;
                const dueToday = taskStats?.due_today ?? 0;
                const blocked = taskStats?.blocked ?? 0;
                const stale = taskStats?.stale_over_7_days ?? 0;
                // "Done this month" keeps the completion metric period-consistent.
                const done = taskStats?.done_this_month ?? stats?.completedTasks ?? 0;
                const segs: Segment[] = [
                  { label: "On track", value: Math.max(0, open - overdue - dueToday), color: "var(--gt-primary)" },
                  { label: "Due today", value: dueToday, color: "var(--gt-prio-med-dot)" },
                  { label: "Overdue", value: overdue, color: "var(--gt-prio-urgent-dot)" },
                  { label: "Done (mo.)", value: done, color: "var(--gt-online)" },
                ];
                const total = segs.reduce((s, x) => s + x.value, 0);
                // Always render the donut — nothing in flight shows 0% over an
                // empty ring rather than a blank "no data" message.
                return (
                  <div className="flex flex-col items-center gap-4">
                    <DonutChart
                      segments={segs}
                      size={140}
                      thickness={16}
                      centerValue={<Counter value={Math.round((done / Math.max(total, 1)) * 100)} suffix="%" />}
                      centerLabel="completed"
                    />
                    <div className="w-full space-y-1.5">
                      {segs.map((s, i) => (
                        <div key={i} className="flex items-center justify-between" style={{ fontSize: 11.5 }}>
                          <span className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
                            <span style={{ color: "var(--gt-text-secondary)" }}>{s.label}</span>
                          </span>
                          <span className="dash-mono" style={{ color: "var(--gt-text-primary)", fontWeight: 600 }}>{s.value}</span>
                        </div>
                      ))}
                    </div>
                    {/* Practical at-a-glance risk signals (surfaced from task stats) */}
                    <div className="w-full grid grid-cols-3 gap-2 pt-3" style={{ borderTop: "1px dashed var(--gt-border-strong)" }}>
                      {[
                        { k: "Overdue", v: overdue, c: "var(--gt-dnd)" },
                        { k: "Blocked", v: blocked, c: "var(--gt-text-primary)" },
                        { k: "Stale >7d", v: stale, c: "var(--gt-away)" },
                      ].map((chip) => (
                        <div key={chip.k}>
                          <div className="dash-eyebrow" style={{ fontSize: 8.5, marginBottom: 3 }}>{chip.k}</div>
                          <div className="dash-serif" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 500, lineHeight: 1, letterSpacing: "-0.02em", color: chip.v > 0 ? chip.c : "var(--gt-text-tertiary)" }}>{chip.v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </button>
            )}

            {/* Leaves breakdown — stacked bar */}
            {cardVisible("dash-leaves-pipeline") && (
            <button
              data-chart="leaves-pipeline"
              onClick={cardNav("dash-leaves-pipeline", "/leaves")}
              className="dash-tile text-left rounded-2xl p-6 lg:col-span-4"
              style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)", display: "flex", flexDirection: "column" }}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="dash-eyebrow mb-1.5">Leaves · Status</div>
                  <h3
                    className="dash-serif"
                    style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 500, letterSpacing: "-0.015em", color: "var(--gt-text-primary)" }}
                  >
                    Leave <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>pipeline</span>
                  </h3>
                </div>
                <div className="text-right">
                  <div className="dash-serif" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 28, fontWeight: 500, letterSpacing: "-0.02em", color: "var(--gt-text-primary)", lineHeight: 1 }}>
                    <Counter value={leavesData.length} />
                  </div>
                  <div className="dash-eyebrow" style={{ fontSize: 9.5, marginTop: 4 }}>total requests</div>
                </div>
              </div>
              {(() => {
                const counts: Record<string, number> = {};
                leavesData.forEach((l: any) => {
                  const s = (l.status || "pending").toLowerCase();
                  counts[s] = (counts[s] || 0) + 1;
                });
                const known: Segment[] = [
                  { label: "Approved", value: counts["approved"] || 0, color: "var(--gt-online)" },
                  { label: "Pending", value: counts["pending"] || 0, color: "var(--gt-away)" },
                  { label: "Rejected", value: counts["rejected"] || 0, color: "var(--gt-dnd)" },
                  { label: "Cancelled", value: counts["cancelled"] || 0, color: "var(--gt-text-tertiary)" },
                ];
                // Any status we don't explicitly track still counts toward the total, so the
                // stacked-bar denominator always matches the "total requests" headline above
                // (this is what fixes the 6-vs-5 mismatch where a cancelled leave was dropped).
                const accountedFor = known.reduce((s, x) => s + x.value, 0);
                const other = Math.max(0, leavesData.length - accountedFor);
                const segs: Segment[] = other > 0
                  ? [...known, { label: "Other", value: other, color: "var(--gt-surface-3)" }]
                  : known;
                // Always render the bar — no applications shows an empty track
                // (0 across all statuses) rather than a blank "no data" message.
                return <StackedBar segments={segs} height={14} />;
              })()}

              {leavesData.length > 0 && (
                <div className="mt-5 pt-4" style={{ borderTop: "1px dashed var(--gt-border-strong)", flex: 1, minHeight: 0 }}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="dash-eyebrow" style={{ fontSize: 9 }}>Recent requests</div>
                    {(() => {
                      const byType: Record<string, number> = {};
                      leavesData.forEach((l: any) => {
                        const t = (l.leaveType || "personal").toLowerCase();
                        byType[t] = (byType[t] || 0) + 1;
                      });
                      const top = Object.entries(byType).sort((a, b) => b[1] - a[1])[0];
                      return top ? (
                        <div className="dash-mono" style={{ fontSize: 9.5, color: "var(--gt-text-tertiary)", textTransform: "capitalize" }}>
                          {top[1]} {top[0]}
                        </div>
                      ) : null;
                    })()}
                  </div>
                  <div className="space-y-2.5">
                    {leavesData.slice(0, 5).map((l: any, i: number) => {
                      const status = (l.status || "pending").toLowerCase();
                      const sc =
                        status === "approved" ? "var(--gt-online)" :
                        status === "rejected" ? "var(--gt-dnd)" :
                        status === "cancelled" ? "var(--gt-text-tertiary)" :
                        "var(--gt-away)";
                      const fmtD = (s: any) => {
                        if (!s) return "";
                        const d = new Date(s);
                        return isNaN(d.getTime()) ? String(s) : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                      };
                      const range = [fmtD(l.startDate), fmtD(l.endDate)].filter(Boolean).join(" – ");
                      return (
                        <div key={l._id || i} className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--gt-text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {l.employeeName || "Employee"}
                            </div>
                            <div className="dash-mono" style={{ fontSize: 10.5, color: "var(--gt-text-tertiary)", textTransform: "capitalize" }}>
                              {(l.leaveType || "leave")}{range ? ` · ${range}` : ""}
                            </div>
                          </div>
                          <span style={{ fontSize: 10, fontWeight: 600, textTransform: "capitalize", color: sc, background: "var(--gt-surface-1)", border: "1px solid var(--gt-border)", padding: "2px 10px", borderRadius: 999, whiteSpace: "nowrap" }}>
                            {status}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </button>
            )}

            {/* Workforce — donut */}
            {cardVisible("dash-workforce") && (
            <button
              data-chart="workforce"
              onClick={cardNav("dash-workforce", "/employees")}
              className="dash-tile text-left rounded-2xl p-6 lg:col-span-2"
              style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
            >
              <div className="dash-eyebrow mb-1.5">Workforce · Composition</div>
              <h3
                className="dash-serif mb-4"
                style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 500, letterSpacing: "-0.015em", color: "var(--gt-text-primary)" }}
              >
                Active <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>workforce</span>
              </h3>
              {(() => {
                const total = stats?.total_employees || 0;
                const active = stats?.activeEmployees || 0;
                const inactive = Math.max(0, total - active);
                const segs: Segment[] = [
                  { label: "Active", value: active, color: "var(--gt-primary)" },
                  { label: "Inactive", value: inactive, color: "var(--gt-surface-3)" },
                ];
                // Always render the donut — no employees shows 0 over an empty
                // ring rather than a blank "no data" message.
                return (
                  <div className="flex flex-col items-center gap-4">
                    <DonutChart
                      segments={segs}
                      size={140}
                      thickness={16}
                      centerValue={<Counter value={total} />}
                      centerLabel="on team"
                    />
                    <div className="w-full flex items-center justify-around" style={{ fontSize: 12 }}>
                      <div className="text-center">
                        <div className="dash-mono" style={{ color: "var(--gt-text-primary)", fontWeight: 700 }}>{active}</div>
                        <div className="dash-eyebrow" style={{ fontSize: 9, marginTop: 2 }}>active</div>
                      </div>
                      <div className="w-px h-8" style={{ background: "var(--gt-border)" }} />
                      <div className="text-center">
                        <div className="dash-mono" style={{ color: "var(--gt-text-primary)", fontWeight: 700 }}>{inactive}</div>
                        <div className="dash-eyebrow" style={{ fontSize: 9, marginTop: 2 }}>inactive</div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </button>
            )}

            {/* Projects — status mix + recent active list (fills the row beside Workforce) */}
            {cardVisible("dash-projects") && (
            <button
              data-chart="projects"
              onClick={cardNav("dash-projects", "/projects")}
              className="dash-tile text-left rounded-2xl p-6 lg:col-span-4"
              style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
            >
              {(() => {
                const items = Array.isArray(projectsList) ? projectsList : [];

                // Bucket by status. Be liberal about field names.
                const norm = (v: any) => (v || "").toString().toLowerCase().replace(/[\s_-]+/g, "");
                const counts = { active: 0, completed: 0, onhold: 0, planned: 0, other: 0 };
                items.forEach((p: any) => {
                  const s = norm(p.status || p.state || p.project_status);
                  if (s === "active" || s === "inprogress" || s === "ongoing") counts.active++;
                  else if (s === "completed" || s === "done" || s === "finished") counts.completed++;
                  else if (s === "onhold" || s === "paused" || s === "blocked") counts.onhold++;
                  else if (s === "planned" || s === "todo" || s === "notstarted" || s === "draft") counts.planned++;
                  else counts.other++;
                });

                const total = items.length;
                const segs: Segment[] = [
                  { label: "Active",    value: counts.active,    color: "var(--gt-primary)" },
                  { label: "Planned",   value: counts.planned,   color: "var(--gt-prio-med-dot)" },
                  { label: "On hold",   value: counts.onhold,    color: "var(--gt-away)" },
                  { label: "Completed", value: counts.completed, color: "var(--gt-online)" },
                ];
                if (counts.other > 0) {
                  segs.push({ label: "Other", value: counts.other, color: "var(--gt-surface-3)" });
                }

                // Pick a few recent active projects.
                const recent = items
                  .filter((p: any) => {
                    const s = norm(p.status || p.state || p.project_status);
                    return !["completed", "done", "finished"].includes(s);
                  })
                  // Busiest projects first — the one with the most tasks at the top.
                  .sort((a: any, b: any) => (b.task_counts?.total ?? 0) - (a.task_counts?.total ?? 0))
                  .slice(0, 5);

                const fmt = (d: any) => {
                  if (!d) return null;
                  const dt = new Date(d);
                  if (isNaN(dt.getTime())) return null;
                  return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
                };

                return (
                  <>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="dash-eyebrow mb-1.5">Projects · Health</div>
                        <h3
                          className="dash-serif"
                          style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 500, letterSpacing: "-0.015em", color: "var(--gt-text-primary)" }}
                        >
                          Active <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>portfolio</span>
                        </h3>
                      </div>
                      <div className="text-right">
                        <div className="dash-serif" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 28, fontWeight: 500, letterSpacing: "-0.02em", color: "var(--gt-text-primary)", lineHeight: 1 }}>
                          <Counter value={total} />
                        </div>
                        <div className="dash-eyebrow" style={{ fontSize: 9.5, marginTop: 4 }}>total projects</div>
                      </div>
                    </div>

                    {/* Always render the bar — no projects shows an empty track
                        (0 across all statuses) rather than a blank message. */}
                    {total === 0 ? (
                      <StackedBar segments={segs} height={14} />
                    ) : (
                      <>
                        <StackedBar segments={segs} height={14} />

                        {recent.length > 0 && (
                          <ul className="mt-5 pt-4 space-y-2.5" style={{ borderTop: "1px dashed var(--gt-border-strong)" }}>
                            {recent.map((p: any, i: number) => {
                              const status = norm(p.status || p.state || p.project_status);
                              const isOnHold = ["onhold", "paused", "blocked"].includes(status);
                              const dotColor = isOnHold ? "var(--gt-away)" : "var(--gt-primary)";
                              const due = fmt(p.endDate || p.end_date || p.dueDate || p.due_date || p.deadline);
                              return (
                                <li
                                  key={p.id || p._id || i}
                                  className="flex items-center justify-between gap-3 py-1"
                                  style={{ borderBottom: i < recent.length - 1 ? "1px dashed var(--gt-border)" : "none" }}
                                >
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: dotColor }} />
                                    <div className="min-w-0">
                                      <div className="truncate" style={{ fontSize: 13, fontWeight: 600, color: "var(--gt-text-primary)" }}>
                                        {p.name || p.project_name || p.title || "Untitled project"}
                                      </div>
                                      <div className="dash-eyebrow" style={{ fontSize: 9.5, marginTop: 1 }}>
                                        {(() => { const tc = p.task_counts?.total ?? 0; return `${tc} task${tc === 1 ? "" : "s"}`; })()} · {(p.status || p.state || "active").toString()}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-right flex-shrink-0">
                                    <div className="dash-serif" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 18, fontWeight: 500, color: "var(--gt-text-primary)", lineHeight: 1, letterSpacing: "-0.02em" }}>
                                      {p.task_counts?.total ?? 0}
                                    </div>
                                    <div className="dash-eyebrow" style={{ fontSize: 9, marginTop: 2 }}>
                                      {due ? `due ${due}` : `${p.task_counts?.open ?? 0} open`}
                                    </div>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </>
                    )}
                  </>
                );
              })()}
            </button>
            )}
          </div>
        </div>

        {/* MODULES GRID — every Teamlio feature in one place */}
        <div>
          <div className="dash-reveal flex items-end justify-between mb-5 flex-wrap gap-3">
            <div>
              <h2
                className="dash-serif"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: "clamp(24px, 2.6vw, 34px)",
                  lineHeight: 1.05,
                  letterSpacing: "-0.02em",
                  fontWeight: 500,
                  color: "var(--gt-text-primary)",
                }}
              >
                Everything <span style={{ fontStyle: "italic" }}>at a glance</span>
              </h2>
            </div>
            <div className="dash-mono" style={{ fontSize: 11, color: "var(--gt-text-tertiary)", letterSpacing: "0.18em", textTransform: "uppercase" }}>
              {isEmployee ? "06 modules" : "09 modules"}
            </div>
          </div>

          <div className="dash-reveal dash-stagger grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {(() => {
              // Compute summaries from real data
              const totalEmp = stats?.total_employees || 0;
              const activeEmp = stats?.activeEmployees || 0;
              const inactiveEmp = Math.max(0, totalEmp - activeEmp);
              const present = stats?.todays_present || 0;
              const away = Math.max(0, totalEmp - present);
              const presentPct = totalEmp > 0 ? Math.round((present / totalEmp) * 100) : 0;

              const leaveCounts = leavesData.reduce((acc: any, l: any) => {
                const s = (l.status || "pending").toLowerCase();
                acc[s] = (acc[s] || 0) + 1;
                return acc;
              }, {});
              const lvPending = leaveCounts.pending || stats?.pending_leaves || 0;
              const lvApproved = leaveCounts.approved || 0;

              const dueToday = taskStats?.due_today || 0;
              const overdue = taskStats?.overdue || 0;
              const openTasks = stats?.openTasks || taskStats?.open || 0;
              const doneTasks = stats?.completedTasks || taskStats?.done_this_month || 0;

              const projActive = projectsList.filter((p) => (p.status || "ACTIVE") === "ACTIVE").length;
              const projOnHold = projectsList.filter((p) => p.status === "ON_HOLD").length;
              const projDone = projectsList.filter((p) => p.status === "COMPLETED").length;
              const projTotal = projectsList.length;

              const slPending = salaryStatus?.pending || 0;
              const slApproved = salaryStatus?.approved || 0;
              const slTotal = salaryStatus?.total || 0;

              const all = [
                {
                  n: "01", title: "Employees", desc: "Profiles, roles, bulk import",
                  Icon: Users, href: "/employees",
                  primary: { v: totalEmp, l: "On board" },
                  secondary: { v: activeEmp, l: "Active" },
                  tertiary: inactiveEmp > 0 ? `${inactiveEmp} inactive` : undefined,
                },
                {
                  n: "02", title: "Attendance", desc: "GPS clock-ins, daily status",
                  Icon: Clock, href: "/attendance",
                  primary: { v: present, l: "Present today" },
                  secondary: { v: away, l: "Away" },
                  tertiary: `${presentPct}% rate`,
                },
                {
                  n: "03", title: "Leaves", desc: "Requests, balances, approvals",
                  Icon: Calendar, href: "/leaves",
                  primary: { v: lvPending, l: "Pending" },
                  secondary: { v: lvApproved, l: "Approved" },
                  tertiary: leavesData.length > 0 ? `${leavesData.length} total` : undefined,
                },
                {
                  n: "04", title: "Daily Planner", desc: "Today / Upcoming / Missed",
                  Icon: ClipboardList, href: "/todo",
                  primary: { v: dueToday, l: "Due today" },
                  secondary: { v: overdue, l: "Overdue" },
                  tertiary: taskStats?.unassigned ? `${taskStats.unassigned} unassigned` : undefined,
                },
                {
                  n: "05", title: "Tasks", desc: "Assign, track, complete",
                  Icon: CheckSquare, href: "/tasks/list",
                  primary: { v: openTasks, l: "Active" },
                  secondary: { v: doneTasks, l: "Done" },
                  tertiary: taskStats?.blocked ? `${taskStats.blocked} blocked` : undefined,
                },
                {
                  n: "06", title: "Projects", desc: "Kanban, clients, milestones",
                  Icon: FolderOpen, href: "/projects", adminOnly: true,
                  primary: { v: projActive, l: "Active" },
                  secondary: { v: projDone, l: "Done" },
                  tertiary: projOnHold > 0 ? `${projOnHold} on hold` : projTotal > 0 ? `${projTotal} total` : undefined,
                },
                {
                  n: "07", title: "Work Monitor", desc: "Activity, screenshots, reports",
                  Icon: Activity, href: "/activity-reports", adminOnly: true,
                  primary: { v: activeEmp, l: "Tracked" },
                  secondary: { v: present, l: "Online today" },
                  tertiary: "Live activity",
                },
                {
                  n: "08", title: "Payroll", desc: "Salaries, payslips, approvals",
                  Icon: Wallet, href: "/payroll-record", adminOnly: true,
                  primary: { v: slApproved, l: "Approved" },
                  secondary: { v: slPending, l: "Pending" },
                  tertiary: slTotal > 0 ? `${slTotal} this month` : new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" }),
                },
                {
                  n: "09", title: "Downloads", desc: "Mobile & desktop apps",
                  Icon: Download, href: "/downloads",
                  primary: { v: 3, l: "Platforms" },
                  secondary: { v: 2, l: "Apps" },
                  tertiary: "iOS · Android · Web",
                },
              ];
              const visible = isEmployee ? all.filter((m) => !m.adminOnly) : all;
              return visible.map((mod) => (
                <button
                  key={mod.n}
                  onClick={() => navigate(mod.href)}
                  className="dash-tile text-left rounded-2xl p-5 relative group"
                  style={{
                    background: "var(--gt-surface-0)",
                    border: "1px solid var(--gt-border)",
                  }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className="dash-mono" style={{ fontSize: 10, letterSpacing: "0.18em", color: "var(--gt-text-tertiary)" }}>
                      № {mod.n}
                    </span>
                    <span
                      className="w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300"
                      style={{ background: "var(--gt-primary-container)", color: "var(--gt-primary-deep)" }}
                    >
                      <mod.Icon className="w-4 h-4" />
                    </span>
                  </div>
                  <div
                    className="dash-serif mb-1"
                    style={{
                      fontFamily: "'Fraunces', Georgia, serif",
                      fontSize: 18,
                      letterSpacing: "-0.015em",
                      lineHeight: 1.15,
                      fontWeight: 500,
                      color: "var(--gt-text-primary)",
                    }}
                  >
                    {mod.title}
                  </div>
                  <div style={{ fontSize: 11.5, color: "var(--gt-text-tertiary)", lineHeight: 1.4, marginBottom: 12 }}>{mod.desc}</div>

                  {/* Two-stat summary */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div>
                      <div
                        className="dash-serif"
                        style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 500, letterSpacing: "-0.025em", color: "var(--gt-text-primary)", lineHeight: 1 }}
                      >
                        <Counter value={Number(mod.primary.v) || 0} />
                      </div>
                      <div className="dash-eyebrow" style={{ fontSize: 8.5, marginTop: 3 }}>{mod.primary.l}</div>
                    </div>
                    <div>
                      <div
                        className="dash-serif"
                        style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 500, letterSpacing: "-0.025em", color: "var(--gt-text-secondary)", lineHeight: 1 }}
                      >
                        <Counter value={Number(mod.secondary.v) || 0} />
                      </div>
                      <div className="dash-eyebrow" style={{ fontSize: 8.5, marginTop: 3 }}>{mod.secondary.l}</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3" style={{ borderTop: "1px dashed var(--gt-border-strong)" }}>
                    <span className="dash-mono" style={{ fontSize: 9.5, color: "var(--gt-primary)", letterSpacing: "0.16em", textTransform: "uppercase" }}>
                      {mod.tertiary || "Open"}
                    </span>
                    <ArrowRight className="dash-arrow w-3.5 h-3.5" style={{ color: "var(--gt-primary)" }} />
                  </div>
                </button>
              ));
            })()}
          </div>
        </div>

        {/* HEALTH ROW — Attendance donut + Org Pulse. Each gated by its dash-* key. */}
        {(cardVisible("dash-attendance-health") || cardVisible("dash-org-pulse")) && (() => {
          const totalEmp = stats?.total_employees || 0;
          const presentToday = stats?.todays_present || 0;
          const attendancePct = totalEmp > 0 ? Math.round((presentToday / totalEmp) * 100) : 0;
          const openTasks = stats?.openTasks || 0;
          const completedTasks = stats?.completedTasks || 0;
          const totalTasks = openTasks + completedTasks;
          const taskCompletionPct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
          const donutR = 56;
          const donutC = 2 * Math.PI * donutR;
          const breakdown = [
            { label: "Present today", count: presentToday, total: totalEmp || 1, color: "var(--gt-online)" },
            { label: "Pending leaves", count: stats?.pending_leaves || 0, total: Math.max(totalEmp || 1, 1), color: "var(--gt-away)" },
            { label: "Active tasks", count: openTasks, total: totalTasks || 1, color: "var(--gt-primary)" },
          ];
          return (
            <div className="dash-reveal grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Attendance donut */}
              {cardVisible("dash-attendance-health") && (
              <button
                data-chart="attendance-health"
                onClick={cardNav("dash-attendance-health", "/attendance")}
                className="dash-tile text-left rounded-2xl p-6 lg:col-span-1 relative"
                style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
              >
                <div className="flex items-center justify-between mb-3">
                  <ArrowRight className="dash-arrow w-3.5 h-3.5" style={{ color: "var(--gt-primary)" }} />
                </div>
                <h3
                  className="dash-serif mb-3"
                  style={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontSize: 20,
                    letterSpacing: "-0.015em",
                    fontWeight: 500,
                    color: "var(--gt-text-primary)",
                  }}
                >
                  Attendance <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>Health</span>
                </h3>
                <div className="flex items-center justify-center" style={{ padding: "8px 0" }}>
                  <div style={{ position: "relative", width: 160, height: 160 }}>
                    <svg width={160} height={160} viewBox="0 0 160 160" style={{ transform: "rotate(-90deg)" }}>
                      <circle cx={80} cy={80} r={donutR} fill="none" stroke="var(--gt-surface-2)" strokeWidth={12} />
                      <circle
                        cx={80}
                        cy={80}
                        r={donutR}
                        fill="none"
                        stroke="var(--gt-primary)"
                        strokeWidth={12}
                        strokeLinecap="round"
                        strokeDasharray={donutC}
                        strokeDashoffset={donutC * (1 - attendancePct / 100)}
                        style={{ transition: "stroke-dashoffset 1200ms cubic-bezier(0.22, 1, 0.36, 1)" }}
                      />
                    </svg>
                    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                      <span
                        className="dash-serif"
                        style={{
                          fontFamily: "'Fraunces', Georgia, serif",
                          fontSize: 40,
                          fontWeight: 500,
                          letterSpacing: "-0.025em",
                          color: "var(--gt-text-primary)",
                          lineHeight: 1,
                        }}
                      >
                        <Counter value={attendancePct} />
                        <span style={{ fontSize: 18, color: "var(--gt-text-tertiary)" }}>%</span>
                      </span>
                      <span className="dash-eyebrow" style={{ marginTop: 4 }}>present</span>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between dash-mono" style={{ marginTop: 12, fontSize: 11.5 }}>
                  <span style={{ color: "var(--gt-online)" }}>● {presentToday} present</span>
                  <span style={{ color: "var(--gt-text-tertiary)" }}>● {Math.max(totalEmp - presentToday, 0)} away</span>
                </div>
              </button>
              )}

              {/* Org Pulse */}
              {cardVisible("dash-org-pulse") && (
              <div
                data-chart="org-pulse"
                className="rounded-2xl p-6 lg:col-span-2 relative"
                style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" style={{ color: "var(--gt-primary)" }} />
                    <h3
                      className="dash-serif"
                      style={{
                        fontFamily: "'Fraunces', Georgia, serif",
                        fontSize: 20,
                        letterSpacing: "-0.015em",
                        fontWeight: 500,
                        color: "var(--gt-text-primary)",
                      }}
                    >
                      Organisation <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>Pulse</span>
                    </h3>
                  </div>
                  <span className="inline-flex items-center gap-1.5 dash-mono" style={{ fontSize: 10, color: "var(--gt-online)", letterSpacing: "0.2em", textTransform: "uppercase" }}>
                    <span className="dash-pulse w-1.5 h-1.5 rounded-full" style={{ background: "var(--gt-online)" }} />
                    Live
                  </span>
                </div>

                <div className="space-y-4">
                  {breakdown.map((row) => {
                    const pct = Math.min(100, Math.round((row.count / row.total) * 100));
                    return (
                      <div key={row.label}>
                        <div className="flex justify-between mb-1.5" style={{ fontSize: 12.5 }}>
                          <span style={{ fontWeight: 600, color: "var(--gt-text-secondary)" }}>{row.label}</span>
                          <span className="dash-mono" style={{ color: "var(--gt-text-tertiary)" }}>
                            <span style={{ color: "var(--gt-text-primary)", fontWeight: 600 }}>{row.count}</span> · {pct}%
                          </span>
                        </div>
                        <div style={{ height: 8, background: "var(--gt-surface-2)", borderRadius: 999, overflow: "hidden" }}>
                          <div
                            style={{
                              height: "100%",
                              borderRadius: 999,
                              background: row.color,
                              width: `${pct}%`,
                              transition: "width 1200ms cubic-bezier(0.22, 1, 0.36, 1)",
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div
                  className="grid grid-cols-2 gap-3 mt-6 pt-5"
                  style={{ borderTop: "1px dashed var(--gt-border-strong)" }}
                >
                  <div>
                    <span className="dash-eyebrow block" style={{ marginBottom: 4 }}>Task completion</span>
                    <span
                      className="dash-serif"
                      style={{
                        fontFamily: "'Fraunces', Georgia, serif",
                        fontSize: 26,
                        fontWeight: 500,
                        letterSpacing: "-0.025em",
                        color: "var(--gt-text-primary)",
                      }}
                    >
                      <Counter value={taskCompletionPct} suffix="%" />
                    </span>
                    <span className="dash-serif" style={{ fontStyle: "italic", fontSize: 12, marginLeft: 6, color: "var(--gt-text-tertiary)" }}>finished</span>
                  </div>
                  <div>
                    <span className="dash-eyebrow block" style={{ marginBottom: 4 }}>Active workforce</span>
                    <span
                      className="dash-serif"
                      style={{
                        fontFamily: "'Fraunces', Georgia, serif",
                        fontSize: 26,
                        fontWeight: 500,
                        letterSpacing: "-0.025em",
                        color: "var(--gt-text-primary)",
                      }}
                    >
                      <Counter value={stats?.activeEmployees || 0} />
                    </span>
                    <span className="dash-serif" style={{ fontStyle: "italic", fontSize: 12, marginLeft: 6, color: "var(--gt-text-tertiary)" }}>of {totalEmp}</span>
                  </div>
                </div>
              </div>
              )}
            </div>
          );
        })()}

        {/* QUICK ACTIONS + ACTIVITIES — employees get them at the very top (order
            -3, above the charts); other roles keep them right under Insights. */}
        {cardVisible("dash-quick-actions") && (
        <div data-chart="quick-actions" className="dash-reveal grid grid-cols-1 lg:grid-cols-3 gap-4" style={{ order: isEmployee ? -3 : -1 }}>
          {/* Quick actions */}
          <div
            className="rounded-2xl p-6"
            style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <PlusCircle className="w-4 h-4" style={{ color: "var(--gt-primary)" }} />
              <h3
                className="dash-serif"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: 18,
                  fontWeight: 500,
                  letterSpacing: "-0.015em",
                  color: "var(--gt-text-primary)",
                }}
              >
                Quick <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>actions</span>
              </h3>
            </div>
            <p style={{ fontSize: 12, color: "var(--gt-text-tertiary)", marginBottom: 18 }}>
              Common tasks and shortcuts
            </p>

            <div className="space-y-2.5">
              {(isEmployee
                ? [
                    { label: "Clock in / out", Icon: Clock, href: "/attendance" },
                    { label: "Apply for leave", Icon: Calendar, href: "/leaves" },
                    { label: "View my tasks", Icon: CheckSquare, href: "/tasks/my-tasks" },
                  ]
                : [
                    { label: "Add an employee", Icon: UserPlus, href: "/employees" },
                    { label: "Approve leaves", Icon: Calendar, href: "/leaves" },
                    { label: "Open reports", Icon: FileText, href: "/tasks/reports" },
                    { label: "Run payroll", Icon: Wallet, href: "/payroll-record" },
                    { label: "Download apps", Icon: Download, href: "/downloads" },
                  ]
              ).map((a, i) => (
                <button
                  key={i}
                  onClick={() => navigate(a.href)}
                  className="dash-tile w-full text-left flex items-center gap-3 rounded-xl px-4 py-3"
                  style={{
                    background: "var(--gt-surface-1)",
                    border: "1px solid var(--gt-border)",
                  }}
                >
                  <span
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: "var(--gt-primary-container)", color: "var(--gt-primary-deep)" }}
                  >
                    <a.Icon className="w-4 h-4" />
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--gt-text-primary)" }}>{a.label}</span>
                  <ArrowRight className="dash-arrow w-3.5 h-3.5 ml-auto" style={{ color: "var(--gt-primary)" }} />
                </button>
              ))}
            </div>
          </div>

          {/* Recent activities — newest first */}
          <div
            className="lg:col-span-2 rounded-2xl p-6"
            style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <span className="dash-pulse w-2 h-2 rounded-full" style={{ background: "var(--gt-online)" }} />
                <Bell className="w-4 h-4" style={{ color: "var(--gt-primary)" }} />
                <h3
                  className="dash-serif"
                  style={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontSize: 18,
                    fontWeight: 500,
                    letterSpacing: "-0.015em",
                    color: "var(--gt-text-primary)",
                  }}
                >
                  Recent <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>activities</span>
                </h3>
              </div>
              {activities.length > 4 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => goToActivityPage(currentPageIndex - 1)}
                    className="p-1 rounded-full transition-colors hover:bg-[var(--gt-surface-1)]"
                    style={{ color: "var(--gt-text-tertiary)" }}
                    aria-label="Previous activities"
                    title="Previous"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="flex gap-1.5">
                    {Array.from({ length: Math.min(5, Math.ceil(activities.length / 4)) }).map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => goToActivityPage(idx)}
                        className="rounded-full transition-all duration-300"
                        style={{
                          width: currentPageIndex === idx ? 22 : 6,
                          height: 6,
                          background: currentPageIndex === idx ? "var(--gt-primary)" : "var(--gt-border-strong)",
                        }}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => goToActivityPage(currentPageIndex + 1)}
                    className="p-1 rounded-full transition-colors hover:bg-[var(--gt-surface-1)]"
                    style={{ color: "var(--gt-primary)" }}
                    aria-label="Next activities"
                    title="Next"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            <p style={{ fontSize: 12, color: "var(--gt-text-tertiary)", marginBottom: 16 }}>
              Attendance · Leaves · Salary · Tasks
            </p>

            {currentDisplay.length === 0 ? (
              <div className="text-center py-10" style={{ color: "var(--gt-text-tertiary)" }}>
                <Bell className="w-9 h-9 mx-auto mb-2" style={{ opacity: 0.4 }} />
                <p className="text-sm">No recent activity</p>
                <p className="text-xs mt-1" style={{ opacity: 0.7 }}>Updates will appear here as your team works</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {currentDisplay.map((activity, idx) => (
                  <button
                    type="button"
                    key={activity.id || `${activity.displayType}-${idx}-${activity.time}`}
                    onClick={() => {
                      const t = (activity.displayType || activity.type || "").toLowerCase();
                      navigate(
                        t.startsWith("leave") ? "/leaves"
                        : t.startsWith("salary") ? "/payroll-record"
                        : t.startsWith("task") ? "/tasks/list"
                        : "/attendance"
                      );
                    }}
                    className="dash-tile w-full text-left flex items-center justify-between p-3.5 rounded-xl group"
                    style={{
                      background: "var(--gt-surface-1)",
                      border: "1px solid var(--gt-border)",
                      animationDelay: `${idx * 0.06}s`,
                      cursor: "pointer",
                    }}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-full flex-shrink-0 ${getActivityColorClass(activity.color)}`}>
                        {getActivityIconComponent(activity.icon)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p style={{ fontSize: 13.5, fontWeight: 600, color: "var(--gt-text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {activity.displayMessage}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="dash-mono" style={{ fontSize: 10.5, color: "var(--gt-text-tertiary)" }}>
                            {getTimeAgo(activity.time || activity.timestamp)}
                          </span>
                          {activity.badge && (
                            <Badge variant="outline" className={`text-xs ${getBadgeColorClass(activity.badgeColor)}`}>
                              {activity.badge}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 dash-arrow" style={{ color: "var(--gt-text-tertiary)" }} />
                  </button>
                ))}
              </div>
            )}

            {activities.length > 4 && (
              <div
                className="mt-5 pt-3 text-center dash-mono"
                style={{ borderTop: "1px dashed var(--gt-border-strong)", fontSize: 10.5, color: "var(--gt-text-tertiary)", letterSpacing: "0.16em", textTransform: "uppercase" }}
              >
                Page {currentPageIndex + 1} of {Math.ceil(activities.length / 4)} · tap ‹ › to navigate
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

// IntersectionObserver runner for .dash-reveal elements
function DashRevealRunner() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-revealed");
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".dash-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return null;

}